import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import "./SyncFullscreen.css";
import TinkTracker from "./TinkTracker";

/**
 * Magic Beacon HLS sync overlay — checkpoint 4g + degrade ladder.
 *
 * 3b/3c/3d/4a/4b/4c/4d/3e/4e/4g kept intact. New in this revision:
 *   - Graded stall-recovery ladder (replaces the single frag-idle
 *     remount-to-black). On a stall the watchdog climbs:
 *       1. quiet recovery (hls.startLoad + bounded micro-hop in place)
 *       2. degrade to 720 (TV only) under a held last frame + overlay
 *       3. graceful remount, still under held frame + overlay
 *   - "Optimizing your stream…" overlay (NO doors imagery) shown during
 *     rung 2/3 recovery; persists until video resumes, then fades out.
 *   - Held-frame source swap: degrade/remount avoid the hard video clear
 *     so the last painted frame stays visible (never black) under the
 *     overlay until the new lane paints.
 *   - One-way degrade: once dropped to 720 for a stream it stays 720 for
 *     the rest of that stream (resets only on streamId change / unmount).
 *
 * Sync safety: showPos is PDT-derived (computeSyncShowPos reads the
 * playing fragment's programDateTime). A lane swap doesn't change
 * streamId or the anchor, and both lanes are server-normalized to the
 * same 720 PDT axis, so showPos stays continuous across a degrade. The
 * cue runner's existing forward-jump guard handles the (bounded) showPos
 * step that results from leaving a stalled-behind playhead.
 */

const BEACON_API_BASE = "https://beacon.mywaitserver.com";

const DOORS_MIN_MS = 6000;
const DOORS_MAX_MS = 30000;
const DOORS_PATIENCE_MS = 15000;
const DOORS_GREEN_HOLD_MS = 1000;
const DOORS_DISSOLVE_MS = 900;
const DOORS_ERROR_FLASH_MS = 3000;

const DOORS_WAKE_MS = 2000;

const HUD_UPDATE_MS = 1000;

const PRESHOW_HANDOFF_WAKE_INTERVAL_MS = 6000;
const PRESHOW_HANDOFF_FIRST_WAKE_DELAY_MS = 2000;
const PRESHOW_HANDOFF_WAKE_TO_CANCEL_GAP_MS = 2000;
const PRESHOW_HANDOFF_CANCEL_TO_CUE_GAP_MS = 2000;
const PRESHOW_HANDOFF_MAX_DURATION_MS = 180_000;

const TICK_MS = 250;
const DRIFT_THRESHOLD_MS = 4000;
const FORWARD_TOLERANCE_MS = 1500;

const WATCHDOG_INTERVAL_MS = 2000;
const FRAG_IDLE_THRESHOLD_MS = 6_000;
const PAUSE_IDLE_THRESHOLD_MS = 3_000;
const PLAYHEAD_NUDGE_THRESHOLD_MS = 8_000;
const PLAYHEAD_REMOUNT_THRESHOLD_MS = 18_000;
const HEALTHY_PLAYBACK_RESET_MS = 30_000;
const LIVE_EDGE_BEHIND_THRESHOLD_S = 6;
const EOS_WEDGE_TOLERANCE_S = 0.5;

// ===== Stall-recovery ladder (retuned 2026-07-14 for the 720 fleet) =====
// Frag-idle escalation, measured from the last successful fragment load
// (lastFragLoadAtRef). Two rungs now — the old middle "degrade to 720"
// rung collapsed into the remount once FORCE_720 pinned the fleet to 720
// (degradeOrRemount720 still routes correctly if 1080 ever returns).
//
//   rung 1 (quiet)   : FRAG_IDLE_THRESHOLD_MS (6s) — hls.startLoad() +
//                      bounded in-place micro-hop. Invisible: only fires
//                      when segments have already stopped; can't touch a
//                      healthy stream. May repeat (cooldown-throttled).
//   rung 2 (remount) : FRAG_IDLE_REMOUNT_MS (14s) — graceful remount under
//                      the held frame + "Optimizing…" overlay. Never black.
//                      (If FORCE_720 is ever flipped off, this rung becomes
//                      the 1080→720 degrade on TV, remount elsewhere.)
//
// Worst case stall→rescue is now ~14-16s (was ~25-31s). Watchdog ticks
// every 2s so detection lag is small.
const FRAG_IDLE_REMOUNT_MS = 14_000;
// Quiet-recovery micro-hop: only hop forward to the nearest healthy
// buffered range when the gap is small enough to stay under the cue
// runner's forward-jump guard (DRIFT_THRESHOLD_MS = 4s). Never live edge.
const MICRO_HOP_MAX_S = 3.0;
// Throttle quiet-recovery startLoad re-issue so we don't hammer it every
// watchdog tick while time escalates toward the remount rung.
const QUIET_RECOVERY_COOLDOWN_MS = 4_000;
// Overlay copy + fade. NO doors imagery — its own dim + message + motion.
const RECOVERING_TEXT = "Optimizing your stream…";
const RECOVERING_FADE_MS = 450;

const BADGE_TICK_MS = 1000;
const MB_COUNTDOWN_DISPLAY_OFFSET_MS = 30_000;
const MB_COUNTDOWN_URGENT_THRESHOLD_MS = 10_000;
const SF_EARLY_LOCKED_HOLD_MS = 4_000;
const SF_EARLY_EXIT_ANIM_MS = 900;

// ===== Fleet resolution switch =====
// FORCE_720 = true pins EVERY player (TV and Display) to the 720 lane,
// regardless of isTv. Decision from the 2026-07-13 congestion night:
// 1080's fat segments are what smash into upstream throughput ceilings
// (droplet→Decodo→googlevideo), while 720 sailed through even the bad
// night. Halving per-viewer video bandwidth doubles concurrency headroom.
// The 1080 lane stays resolved and dormant server-side — to re-enable,
// flip this to false and redeploy. (The old manual res pill + UP-key
// toggle were removed when this went in; see git/backup history.)
const FORCE_720 = false;

const MB_SYNCING_PHASES_DEFAULT = {
  initialMs: 2 * 60 * 1000,
  extendedMs: 1 * 60 * 1000,
};
const MB_SYNCING_PHASES_PER_SHOW = {
  luminous: { initialMs: 5 * 60 * 1000, extendedMs: 2 * 60 * 1000 },
};
function getSyncingPhases(showId) {
  return MB_SYNCING_PHASES_PER_SHOW[showId] || MB_SYNCING_PHASES_DEFAULT;
}

const MB_PRETTY_SHOW = {
  "happily-ever-after": "Happily Ever After",
  luminous: "Luminous",
  fantasmic: "Fantasmic",
  "fantasmic-dl": "Fantasmic",
  "wondrous-journeys": "Wondrous Journeys",
  "world-of-color": "World of Color",
  "not-so-spooky-spectacular": "MK Halloween Show",
  "xmas-mk": "MK Xmas Show",
};

function prettyShow(showId) {
  if (!showId) return "—";
  return (
    MB_PRETTY_SHOW[showId] ||
    showId
      .split("-")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ")
  );
}

function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * TV-only EXIT button. Lives at top-right of fullscreen HLS.
 *
 * Why this is a sub-component with state, not just a styled button:
 * the Enter keypress that triggered sync entry (e.g. confirming
 * "1-screen" in the TV grid-config modal) is still propagating when
 * SyncFullscreen mounts a frame later. If we autoFocused the button
 * immediately, that lingering Enter would land on it and fire onExit
 * — bouncing the user straight back out of sync.
 *
 * Mitigation: a 600ms "armed" grace window during which we focus the
 * button (so the remote ring is there visually) but reject any Enter
 * presses. After 600ms the button accepts Select normally.
 */
function SfTvExitButton({ onExit, peerRef, outerRef }) {
  const btnRef = useRef(null);
  const armedRef = useRef(false);

  // Mirror our button node into a parent-owned ref so the TAKE CONTROL
  // button can ArrowLeft/ArrowRight to us and back.
  useEffect(() => {
    if (outerRef) outerRef.current = btnRef.current;
    return () => {
      if (outerRef) outerRef.current = null;
    };
  });

  useEffect(() => {
    // Focus after the triggering Enter has fully released.
    const focusTimer = setTimeout(() => {
      btnRef.current && btnRef.current.focus();
    }, 250);
    // Arm the button (accept Select) after a slightly longer grace.
    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, 600);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(armTimer);
    };
  }, []);

  const fireExit = (e) => {
    if (!armedRef.current) {
      // Swallow early presses (the lingering Enter from sync entry).
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onExit && onExit();
  };

  return (
    <button
      ref={btnRef}
      type="button"
      className="sf-tv-exit-btn"
      onClick={(e) => {
        if (!armedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onExit && onExit();
      }}
      onKeyDown={(e) => {
        // Trap focus: the EXIT button is the ONLY remote-navigable
        // affordance during HLS — there's nothing else to focus on.
        // Arrow keys / Tab / Back are swallowed so focus can't escape
        // to the embeds underneath. Only Select (Enter/Space) acts.
        if (e.key === "Enter" || e.key === " ") {
          fireExit(e);
          return;
        }
        if (e.key === "ArrowDown") {
          const ctrlEl =
            (peerRef && peerRef.current) ||
            document.querySelector(".sf-tv-takectrl-btn");
          if (ctrlEl) {
            // TAKE CONTROL badge is bottom-right — Down moves the remote to it.
            e.preventDefault();
            e.stopPropagation();
            ctrlEl.focus();
            return;
          }
          // No control badge → fall through to the normal swallow below.
        }
        if (
          e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "Tab" ||
          e.key === "Escape"
        ) {
          e.preventDefault();
          e.stopPropagation();
          // Re-grab in case the browser already moved focus before our
          // handler ran (Tab does this).
          btnRef.current && btnRef.current.focus();
        }
      }}
      onBlur={() => {
        // Always reclaim focus on blur — without this, a re-render or
        // iframe steal during the 600ms grace would orphan the button
        // (visible but unfocusable). The armedRef gate still protects
        // Select from firing during the grace, so reclaiming focus
        // here is safe — the user can't accidentally dismiss early.
        setTimeout(() => {
          const a = document.activeElement;
          const ctrlEl =
            (peerRef && peerRef.current) ||
            document.querySelector(".sf-tv-takectrl-btn");
          if (a && (a === ctrlEl || a === btnRef.current)) {
            return; // focus is on one of our two buttons — leave it
          }
          btnRef.current && btnRef.current.focus();
        }, 0);
      }}
    >
      EXIT
    </button>
  );
}
/**
 * TV-only TAKE CONTROL button for the red control badge. Same focus/arm
 * pattern as SfTvExitButton, but autofocuses at 300ms (deliberately AFTER
 * EXIT's 250ms) so it wins the initial focus race when both mount
 * together. Right moves to EXIT; EXIT's Left moves back here. Each side's
 * blur-reclaim yields when focus landed on the other.
 */
function SfTvTakeControlBubble({ onTake, exitRef, selfRef, interactive, className, children }) {
  const btnRef = useRef(null);
  const armedRef = useRef(false);

  useEffect(() => {
    if (selfRef) selfRef.current = btnRef.current;
    return () => {
      if (selfRef) selfRef.current = null;
    };
  });

  useEffect(() => {
    const focusT = setTimeout(
      () => btnRef.current && btnRef.current.focus(),
      300
    );
    const armT = setTimeout(() => {
      armedRef.current = true;
    }, 600);
    return () => {
      clearTimeout(focusT);
      clearTimeout(armT);
    };
  }, []);

  const fire = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!armedRef.current || !interactive) return;
    onTake && onTake();
  };

  return (
    <div
      ref={btnRef}
      role="button"
      className={className + " sf-tv-takectrl-btn"}
      tabIndex={0}
      onClick={fire}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          fire(e);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          const exitEl =
            (exitRef && exitRef.current) ||
            document.querySelector(".sf-tv-exit-btn");
          if (exitEl) exitEl.focus();
          return;
        }
        if (
          e.key === "ArrowLeft" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowRight" ||
          e.key === "Tab" ||
          e.key === "Escape"
        ) {
          e.preventDefault();
          e.stopPropagation();
          btnRef.current && btnRef.current.focus();
        }
      }}
      onBlur={() => {
        setTimeout(() => {
          const a = document.activeElement;
          const exitEl =
            (exitRef && exitRef.current) ||
            document.querySelector(".sf-tv-exit-btn");
          if (a && (a === exitEl || a === btnRef.current)) {
            return; // focus is on one of our two buttons — leave it
          }
          btnRef.current && btnRef.current.focus();
        }, 0);
      }}
      >
      {children}
    </div>
  );
}

/**
 * TV-only two-button row for the doors error card: TRY AGAIN + EXIT.
 * Same focus model as the peer overlay: Left/Right move between the two
 * buttons, Enter/Space fires the focused one, all other keys swallowed so
 * focus can't escape into the embeds. TRY AGAIN autofocuses after a 250ms
 * settle and arms after 600ms, so the keypress that surfaced the error
 * card can't immediately fire either action.
 */
function SfTvErrorButtons({ onRetry, onExit }) {
  const [idx, setIdx] = useState(0); // 0 = TRY AGAIN, 1 = EXIT
  const retryRef = useRef(null);
  const exitRef = useRef(null);
  const armedRef = useRef(false);

  useEffect(() => {
    const focusT = setTimeout(() => retryRef.current && retryRef.current.focus(), 250);
    const armT = setTimeout(() => {
      armedRef.current = true;
    }, 600);
    return () => {
      clearTimeout(focusT);
      clearTimeout(armT);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (k === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        setIdx(0);
        retryRef.current && retryRef.current.focus();
        return;
      }
      if (k === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setIdx(1);
        exitRef.current && exitRef.current.focus();
        return;
      }
      if (k === "Enter" || k === " ") {
        e.preventDefault();
        e.stopPropagation();
        if (!armedRef.current) return;
        if (idx === 0) onRetry && onRetry();
        else onExit && onExit();
        return;
      }
      if (k === "ArrowUp" || k === "ArrowDown" || k === "Tab" || k === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [idx, onRetry, onExit]);

  const btnStyle = (active, bg) => ({
    fontFamily: "Lato, sans-serif",
    fontSize: "1.1vw",
    fontWeight: 900,
    color: "#fff",
    background: bg,
    border: "none",
    borderRadius: "8px",
    padding: "1vh 1.8vw",
    cursor: "pointer",
    outline: active ? "4px solid #05f651" : "4px solid transparent",
  });

  return (
    <div
      style={{
        display: "flex",
        gap: "1.4vw",
        justifyContent: "center",
        marginTop: "1.5vh",
      }}
    >
      <button
        ref={retryRef}
        type="button"
        tabIndex={0}
        onFocus={() => setIdx(0)}
        onClick={() => {
          if (!armedRef.current) return;
          onRetry && onRetry();
        }}
        style={btnStyle(idx === 0, "#2563eb")}
      >
        TRY AGAIN
      </button>
      <button
        ref={exitRef}
        type="button"
        tabIndex={0}
        onFocus={() => setIdx(1)}
        onClick={() => {
          if (!armedRef.current) return;
          onExit && onExit();
        }}
        style={btnStyle(idx === 1, "#dc2626")}
      >
        EXIT
      </button>
    </div>
  );
}

/**
 * TV-only HLS peer-handoff overlay.
 *
 * Replaces the gold .sf-peer-bubble (suppressed on TV) and the
 * tappable stream-warning bar's peer-CTA. Gold/yellow card centered
 * over the HLS view with YES/NO buttons. Focus-trapped (Left/Right
 * move between buttons; Up/Down/Tab swallowed). YES autofocuses
 * after a 600ms grace so the keypress that triggered the overlay
 * (typically Enter mid-session, or a remote button that bubbled in)
 * doesn't immediately confirm the handoff.
 *
 * Props:
 *   peerName     — "@handle" display name for the offering streamer
 *   subtitle     — body copy ("Switch to ... to watch sooner?", or
 *                  the streamer-issue variant)
 *   title        — header copy (default "ANOTHER STREAMER IS READY")
 *   onAccept     — called when YES fires
 *   onDecline    — called when NO fires (or Escape)
 */
function SfTvPeerOverlay({
  peerName,
  subtitle,
  title = "ANOTHER STREAMER IS READY",
  accent = "yellow",
  placement = "corner",
  onAccept,
  onDecline,
}) {
  const [idx, setIdx] = useState(0); // 0 = YES, 1 = NO
  const yesRef = useRef(null);
  const noRef = useRef(null);
  const armedRef = useRef(false);

  useEffect(() => {
    // Focus YES after the triggering key has released. Arm slightly
    // later so Enter during the grace window does nothing.
    const focusT = setTimeout(() => yesRef.current && yesRef.current.focus(), 250);
    const armT = setTimeout(() => {
      armedRef.current = true;
    }, 600);
    return () => {
      clearTimeout(focusT);
      clearTimeout(armT);
    };
  }, []);

  // Window-level focus trap: arrows/Tab swallowed, only Left/Right
  // navigate between YES and NO. Enter/Space fires the focused one.
  // Escape declines. Uses capture phase so we win over the main grid
  // and overlay handlers.
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (k === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        setIdx(0);
        yesRef.current && yesRef.current.focus();
        return;
      }
      if (k === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setIdx(1);
        noRef.current && noRef.current.focus();
        return;
      }
      if (k === "Enter" || k === " ") {
        e.preventDefault();
        e.stopPropagation();
        if (!armedRef.current) return;
        if (idx === 0) onAccept && onAccept();
        else onDecline && onDecline();
        return;
      }
      if (k === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onDecline && onDecline();
        return;
      }
      // Swallow all other keys so focus can't escape upward.
      if (
        k === "ArrowUp" ||
        k === "ArrowDown" ||
        k === "Tab"
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [idx, onAccept, onDecline]);

  // Color tokens by alert severity. Yellow = informational ("another
  // streamer is ready"), red = active issue ("streamer issue
  // persisting"). Both use a dark interior with a colored outline +
  // glow so the show keeps playing visibly behind the card.
  //
  // EXCEPTION: peer-offer (center placement + yellow accent) uses lime
  // green instead of cream-yellow. It's a friendly offer, not an issue —
  // stream-warning yellow stays yellow so the visual language for
  // "something's wrong" reads at a glance.
  const isPeerOffer = placement === "center" && accent === "yellow";
  const accentColor = isPeerOffer
    ? "#05f651"
    : accent === "red"
    ? "#dc2626"
    : "#facc15";
  const accentGlow = isPeerOffer
    ? "rgba(5,246,81,0.55)"
    : accent === "red"
    ? "rgba(220,38,38,0.55)"
    : "rgba(250,204,21,0.55)";
  const accentSoftBg =
    accent === "red"
      ? "rgba(60,12,12,0.92)"
      : "rgba(40,32,12,0.92)";

  // Placement variants:
  //   'corner' — compact upper-right card, used mid-show for stream-
  //              health alerts so the show stays largely visible.
  //   'center' — slightly larger centered card, used for pre-show /
  //              sync-wait peer offers when there's no live show yet
  //              to obscure. Still outlined-on-dark (not filled).
  const isCenter = placement === "center";

  const wrapperStyle = isCenter
    ? {
        position: "fixed",
        inset: 0,
        zIndex: 4600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Subtle dim, not full blackout — still see the embed behind.
        background: "rgba(6,6,16,0.55)",
        fontFamily: "Lato, sans-serif",
        pointerEvents: "auto",
      }
    : {
        // Corner placement: top tuned to 9vh so the card sits BELOW
        // the in-fullscreen Magic Beacon Connected badge (top: 3vh)
        // and matches the no-peer .sf-stream-warning position on TV.
        position: "fixed",
        top: "9vh",
        right: "2.4vw",
        zIndex: 4600,
        fontFamily: "Lato, sans-serif",
        pointerEvents: "auto",
      };

  const cardStyle = isCenter
    ? {
        background: accentSoftBg,
        padding: "3vh 3vw",
        borderRadius: "18px",
        border: `4px solid ${accentColor}`,
        boxShadow: `0 0 40px ${accentGlow}, 0 20px 60px rgba(0,0,0,0.6)`,
        maxWidth: "50vw",
        minWidth: "34vw",
        textAlign: "center",
      }
    : {
        background: accentSoftBg,
        padding: "1.6vh 1.6vw",
        borderRadius: "14px",
        border: `3px solid ${accentColor}`,
        boxShadow: `0 0 24px ${accentGlow}, 0 12px 36px rgba(0,0,0,0.55)`,
        maxWidth: "32vw",
        minWidth: "22vw",
        textAlign: "right",
      };

  const titleStyle = isCenter
    ? {
        fontSize: "1.6vw",
        fontWeight: 900,
        color: accentColor,
        marginBottom: "1vh",
        letterSpacing: "1px",
      }
    : {
        fontSize: "1.1vw",
        fontWeight: 900,
        color: accentColor,
        marginBottom: "0.4vh",
        letterSpacing: "1px",
      };

  const subtitleStyle = isCenter
    ? {
        fontSize: "1.3vw",
        fontWeight: 700,
        color: "#fff",
        marginBottom: "2.4vh",
      }
    : {
        fontSize: "1vw",
        fontWeight: 700,
        color: "#fff",
        marginBottom: "1.4vh",
      };

  const buttonRowJustify = isCenter ? "center" : "flex-end";
  const buttonRowGap = isCenter ? "1.4vw" : "0.8vw";

  return (
    <div style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={titleStyle}>{title}</div>
        <div style={subtitleStyle}>{subtitle}</div>
        <div
          style={{
            display: "flex",
            gap: buttonRowGap,
            justifyContent: buttonRowJustify,
          }}
        >
          <button
            ref={yesRef}
            type="button"
            tabIndex={0}
            onFocus={() => setIdx(0)}
            onClick={() => {
              if (!armedRef.current) return;
              onAccept && onAccept();
            }}
            style={{
              fontFamily: "Lato, sans-serif",
              fontSize: "0.95vw",
              fontWeight: 900,
              // Blue YES button with white letters keeps the lime-green
              // focus outline visually distinct (focus ring vs button
              // color no longer competing in the same green family).
              color: "#fff",
              background: "#2563eb",
              border: "none",
              borderRadius: "8px",
              padding: "0.8vh 1.4vw",
              cursor: "pointer",
              outline:
                idx === 0 ? "4px solid #05f651" : "4px solid transparent",
            }}
          >
            YES, SWITCH
          </button>
          <button
            ref={noRef}
            type="button"
            tabIndex={0}
            onFocus={() => setIdx(1)}
            onClick={() => {
              if (!armedRef.current) return;
              onDecline && onDecline();
            }}
            style={{
              fontFamily: "Lato, sans-serif",
              fontSize: "0.95vw",
              fontWeight: 900,
              color: "#fff",
              background: "#dc2626",
              border: "none",
              borderRadius: "8px",
              padding: "0.8vh 1.4vw",
              cursor: "pointer",
              outline:
                idx === 1 ? "4px solid #05f651" : "4px solid transparent",
            }}
          >
            NO, STAY
          </button>
        </div>
      </div>
    </div>
  );
}

function SyncFullscreen({
  streamId,
  isTv,
  streamerName,
  deviceId,
  sendBeaconCmd,
  sendCancel,
  anchor,
  cues,
  graceModeActive,
  onShowPosTick,
  earlyEntry,
  earlyEntryShowId,
  showtimeMs,
  hlsVolume,
  hlsMuted,
  tinkUserEnabled,
  findLockedPeer,
  onPeerHandoff,
  isController,
  hasHolder,
  holder,
  controlSynced,
  controlKeyReady,
  claimControl,
  onExit,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const networkRecoveryAttemptsRef = useRef(0);
  const lastNetworkRecoveryAtRef = useRef(0);
  const chimeRef = useRef(null);
  const connectionChimeRef = useRef(null);
  const doorsWakeIntervalRef = useRef(null);

  const currentFragRef = useRef(null);
  const anchorRef = useRef(null);
  const cuesRef = useRef([]);
  const deviceIdRef = useRef(null);
  const sendBeaconCmdRef = useRef(null);
  const sendCancelRef = useRef(null);

  const doorsPhaseRef = useRef("closing");
  const graceModeActiveRef = useRef(false);
  const onShowPosTickRef = useRef(null);

  const lastPlayheadAdvanceRef = useRef(0);
  const lastFragLoadAtRef = useRef(0);
  const eosWedgeStartedAtRef = useRef(null);

  const [remountCounter, setRemountCounter] = useState(0);

  // ===== Degrade ladder state =====
  // degradedRef: once true, this stream is pinned to 720 for the rest of
  // the session (one-way). Reset only when streamId changes (handled in
  // the mount effect) or the component unmounts. The mount effect's
  // fetchManifest + MANIFEST_PARSED both read this to pull/target 720.
  const degradedRef = useRef(false);
  // Tracks the streamId the mount effect last ran for, so we reset
  // degradedRef ONLY on a genuine stream change (not on remountCounter
  // bumps, which re-run the effect for degrades/remounts where degraded
  // must persist).
  const lastMountStreamIdRef = useRef(null);
  // softSwapRef: set true right before a degrade/remount that should
  // PRESERVE the last painted frame (no hard video clear in teardown).
  // The mount-effect cleanup checks this and skips pause/removeAttribute/
  // load so the old frame stays on screen under the overlay until the new
  // lane paints. Auto-resets after the cleanup consumes it.
  const softSwapRef = useRef(false);
  // Throttle for rung-1 quiet recovery startLoad re-issue.
  const lastQuietRecoveryAtRef = useRef(0);
  // "Optimizing your stream…" overlay. visible drives mount; fading drives
  // the fade-out class. Shown on rung 2/3, cleared when video resumes.
  const [recovering, setRecovering] = useState({ visible: false, fading: false });
  // ===== Quick-swap offer (2026-07-14) =====
  // When THIS stream has needed rescue twice (remountCounter >= 2) and a
  // HEALTHY peer is locked on the same show (findLockedPeer is segHealth-
  // filtered in useBeaconCore), offer a one-tap switch. One-shot per
  // stream session: once the user answers — either way — it never re-pops
  // (quickSwapAnsweredRef). A "no" also adds the peer to dismissedPeersRef
  // so the other peer-offer flows won't re-offer the same peer either.
  // On a systemic bad night every stream's segHealth is red, findLockedPeer
  // returns null, and this never appears anywhere — by construction.
  const [quickSwapOffer, setQuickSwapOffer] = useState(null); // {streamId, name} | null
  const quickSwapAnsweredRef = useRef(false);
  // ===== Take-control badge (multi-device) =====
  // Red badge replaces the purple CONNECTED badge when another device
  // holds the beacon lock. Gated on controlSynced + controlKeyReady so a
  // booting device (isController false until identity loads) never
  // flashes red spuriously — same guard as Band+'s Tink auto-off.
  const tvExitBtnRef = useRef(null);
  const tvTakeCtrlRef = useRef(null);
  // Badge lifecycle: hidden → (700ms beat) → pop (sound + pop-in anim)
  // → shown → [claim ok] → granted (same bubble turns green) →
  // (1.6s beat) → slideout (slides off right) → hidden → purple badge.
  const [ctrlPhase, setCtrlPhase] = useState("hidden");
  const ctrlPhaseRef = useRef("hidden");
  ctrlPhaseRef.current = ctrlPhase;
  const ctrlTimersRef = useRef([]);
  const claimingRef = useRef(false);
  const ctrlTimer = (fn, ms) => {
    const t = setTimeout(fn, ms);
    ctrlTimersRef.current.push(t);
    return t;
  };
  const needsControl =
    !!claimControl &&
    controlSynced &&
    controlKeyReady &&
    hasHolder &&
    !isController;
  
  
  const handleTakeControl = async () => {
    if (claimingRef.current || ctrlPhaseRef.current === "granted") return;
    claimingRef.current = true;
    try {
      const r = await claimControl();
      if (r && r.ok) {
        try {
          const chime = connectionChimeRef.current;
          if (chime) {
            chime.volume = 0.5;
            chime.currentTime = 0;
            chime.play().catch(() => {});
          }
        } catch {}
        setCtrlPhase("granted");
        ctrlTimer(() => {
          setCtrlPhase("slideout");
          ctrlTimer(() => setCtrlPhase("hidden"), 480);
        }, 2600);
        // TV: the red badge (and its button) unmounts on success — hand
        // the remote back to EXIT so focus isn't orphaned on <body>.
        if (isTv)
          setTimeout(
            () => tvExitBtnRef.current && tvExitBtnRef.current.focus(),
            60
          );
      } else {
        console.error(
          "[beacon] take-control claim failed:",
          r && r.reason
        );
      }
    } finally {
      claimingRef.current = false;
    }
  };
  const recoveringRef = useRef(false);
  useEffect(() => { recoveringRef.current = recovering.visible; }, [recovering.visible]);
  const recoverFadeTimerRef = useRef(null);

  const earlyEntryRef = useRef(false);
  const earlyEntryShowIdRef = useRef(null);
  const showtimeMsRef = useRef(null);
  const syncingStartedAtRef = useRef(0);

  useEffect(() => { anchorRef.current = anchor; }, [anchor]);
  useEffect(() => { cuesRef.current = cues || []; }, [cues]);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  useEffect(() => { sendBeaconCmdRef.current = sendBeaconCmd; }, [sendBeaconCmd]);
  useEffect(() => { sendCancelRef.current = sendCancel; }, [sendCancel]);
  useEffect(() => { graceModeActiveRef.current = graceModeActive; }, [graceModeActive]);
  useEffect(() => { onShowPosTickRef.current = onShowPosTick; }, [onShowPosTick]);
  useEffect(() => { earlyEntryRef.current = earlyEntry; }, [earlyEntry]);
  const findLockedPeerRef = useRef(null);
  useEffect(() => { findLockedPeerRef.current = findLockedPeer; }, [findLockedPeer]);
  useEffect(() => { earlyEntryShowIdRef.current = earlyEntryShowId; }, [earlyEntryShowId]);
  useEffect(() => { showtimeMsRef.current = showtimeMs; }, [showtimeMs]);

  // Helper: clear the "Optimizing…" overlay with a fade, if it's showing.
  // Called when video resumes (FRAG_LOADED / playing) after a degrade or
  // graceful remount. Idempotent.
  const clearRecoveringOverlay = () => {
    if (!recoveringRef.current) return;
    recoveringRef.current = false;
    setRecovering({ visible: true, fading: true });
    if (recoverFadeTimerRef.current) clearTimeout(recoverFadeTimerRef.current);
    recoverFadeTimerRef.current = setTimeout(() => {
      setRecovering({ visible: false, fading: false });
    }, RECOVERING_FADE_MS);
  };

  // Helper: show the "Optimizing…" overlay immediately (no fade-in delay —
  // it needs to cover the held frame the instant recovery starts).
  const showRecoveringOverlay = () => {
    if (recoverFadeTimerRef.current) {
      clearTimeout(recoverFadeTimerRef.current);
      recoverFadeTimerRef.current = null;
    }
    recoveringRef.current = true;
    setRecovering({ visible: true, fading: false });
  };

  const preshowHandoffRef = useRef({
    active: false,
    startedAt: 0,
    wakeCount: 0,
    lastWakeAt: 0,
    cancelScheduledFor: 0,
    wakeIntervalId: null,
    cancelTimerId: null,
    firstWakeTimerId: null,
  });

  // 4c-fix: separate ref for the 2000ms "late-lock" trigger that schedules
  // startPreshowHandoff after doors complete. Lives outside the doorsPhase
  // useEffect so phase transitions ('ready' → 'revealed' → 'hidden') don't
  // cancel it via the effect's cleanup. Mirrors multitest2 line 2562 where
  // the setTimeout is fired inside mbDoorsOpen('success') itself, separate
  // from any subsequent animation timers.
  const preshowTriggerTimerRef = useRef(null);

  const cueRunnerRef = useRef({
    cursor: 0,
    fired: 0,
    skipped: 0,
    cancelled: 0,
    history: [],
    lastShowId: null,
    lastShowPos: null,
  });

  const [doorsPhase, setDoorsPhase] = useState("closing");
  // Retry counter for the doors error card's "Try Again" button. Bumping
  // it re-runs the doors lifecycle effect (fresh timers, phase back to
  // "closing") alongside a remount (fresh manifest fetch) — the same
  // out-and-back-in that fixes these transient load failures 99% of the
  // time, without the user leaving sync.
  const [doorsAttempt, setDoorsAttempt] = useState(0);
  const [showPatience, setShowPatience] = useState(false);
  const [hudData, setHudData] = useState(null);
  const lastHudKeyRef = useRef("");

  const [badgePhase, setBadgePhase] = useState("hidden");
  const [badgeText, setBadgeText] = useState(null);
  const lockChimeFiredRef = useRef(false);
  const badgePhaseRef = useRef("hidden");
  useEffect(() => { badgePhaseRef.current = badgePhase; }, [badgePhase]);

  // 5b: peer-offer bubble. Appears during the syncing wait (badgePhase
  // "syncing", doors finished) when another streamer is already locked on
  // the same show. Tapping it runs onPeerHandoff(peerStreamId). Mirrors
  // multitest2 #sfPeerBubble (line 1899) + sfEarlyBadgeTick logic (5113).
  //   peerBubble.streamId — target for the handoff; null = hidden
  //   peerBubble.exiting  — true while playing the pop-out animation
  const [peerBubble, setPeerBubble] = useState({
    streamId: null,
    exiting: false,
  });
  const peerBubbleShownRef = useRef(false); // gates the one-shot pop sound
  const peerPopRef = useRef(null);
  // Drive the badge on/off from live control state. granted/slideout are
  // owned by handleTakeControl — claiming flips needsControl false, and
  // that's success, not dismissal, so don't interrupt those phases here.
  useEffect(() => {
    if (needsControl && doorsPhase === "hidden") {
      if (ctrlPhaseRef.current === "hidden") {
        ctrlTimer(() => {
          setCtrlPhase("pop");
          try {
            const pop = peerPopRef.current;
            if (pop) {
              pop.currentTime = 0;
              pop.play().catch(() => {});
            }
          } catch {}
          ctrlTimer(() => {
            if (ctrlPhaseRef.current === "pop") setCtrlPhase("shown");
          }, 380);
        }, 700);
      }
    } else if (
      ctrlPhaseRef.current === "pop" ||
      ctrlPhaseRef.current === "shown"
    ) {
      setCtrlPhase("hidden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsControl, doorsPhase]);
  const streamIdRef = useRef(null);
  useEffect(() => { streamIdRef.current = streamId; }, [streamId]);

  // 5d: latest cue-runner showPos (seconds), read by the Tink Tracker.
  // Ref (not state) so cue-tick frequency updates don't re-render the
  // whole sync screen — TinkTracker polls it on its own cadence.
  const tinkShowPosRef = useRef(null);

  // 5c: stream-health warning bar. Driven by anchor.streamHealthWarning
  // (0 = healthy, 1 = readjusting/yellow, 2 = failing/red), which the
  // backend stamps on the anchor and arrives via the parent's anchor poll.
  // When a stage is active AND a peer is locked on the same show, the bar
  // becomes a one-tap handoff. Mirrors multitest2 updateStreamHealthWarning
  // (line 6733) + .sf-stream-warning CSS (818).
  //   stage     — 0 | 1 | 2
  //   peerId    — peer streamId if a handoff is available, else null
  //   shake     — true briefly to (re)fire the buzz shake animation
  const [streamWarning, setStreamWarning] = useState({
    stage: 0,
    peerId: null,
    shake: false,
  });
  const warnShownStageRef = useRef(0); // gates the one-shot buzz per stage
  const warnBuzzRef = useRef(null);
  const warnShakeTimerRef = useRef(null);
  // Peers the user has dismissed with "NO, STAY". Once dismissed for this
  // session, the same peer won't re-prompt — without this, every anchor
  // update that re-detected the same peer would re-pop the overlay.
  // Cleared when sync exits (component unmounts → ref resets).
  const dismissedPeersRef = useRef(new Set());

  // 5c: react to anchor.streamHealthWarning changes. Compute stage + peer
  // availability, fire the one-shot buzz/shake on first appearance or a
  // stage change (1→2), and clear when healthy. Mirrors multitest2
  // updateStreamHealthWarning (line 6733).
  useEffect(() => {
    const stage = (anchor && anchor.streamHealthWarning) || 0;

    if (stage !== 1 && stage !== 2) {
      // Healthy — clear warning + reset gates.
      warnShownStageRef.current = 0;
      setStreamWarning((prev) =>
        prev.stage === 0 ? prev : { stage: 0, peerId: null, shake: false }
      );
      return;
    }

    // Is a peer locked on the same show? (turns the bar into a handoff)
    const showId =
      (anchor && anchor.showId) || earlyEntryShowIdRef.current || null;
    const fn = findLockedPeerRef.current;
    const peer = fn ? fn(showId, streamIdRef.current) : null;
    let peerId = peer ? peer.streamId : null;
    // Respect a prior "NO, STAY" — if the user dismissed this exact peer
    // already, treat as no-peer-offer so the overlay doesn't re-pop.
    if (peerId && dismissedPeersRef.current.has(peerId)) {
      peerId = null;
    }

    // One-shot buzz on first appearance or stage change.
    const firstOrChanged = warnShownStageRef.current !== stage;
    warnShownStageRef.current = stage;

    setStreamWarning({ stage, peerId, shake: firstOrChanged });

    if (firstOrChanged) {
      // restart the shake animation cleanly, then clear the flag
      if (warnShakeTimerRef.current) clearTimeout(warnShakeTimerRef.current);
      warnShakeTimerRef.current = setTimeout(() => {
        setStreamWarning((prev) => ({ ...prev, shake: false }));
      }, 650); // just past the 0.6s shake animation
      try {
        if (warnBuzzRef.current) {
          warnBuzzRef.current.volume = 0.7;
          warnBuzzRef.current.currentTime = 0;
          warnBuzzRef.current.play().catch(() => {});
        }
      } catch {}
    }
  }, [anchor, findLockedPeer]);

  // 5b: hide the peer bubble with the pop-out animation, then unmount it.
  // Mirrors multitest2 hidePeerBubble (line 3458). Idempotent — safe to
  // call every tick. Resets the shown gate so a later re-appearance pops
  // (and sounds) fresh.
  const peerHideTimerRef = useRef(null);
  const maybeHidePeerBubble = () => {
    if (!peerBubbleShownRef.current) return;
    peerBubbleShownRef.current = false;
    setPeerBubble((prev) =>
      prev.streamId ? { streamId: prev.streamId, exiting: true } : prev
    );
    if (peerHideTimerRef.current) clearTimeout(peerHideTimerRef.current);
    peerHideTimerRef.current = setTimeout(() => {
      setPeerBubble({ streamId: null, exiting: false });
    }, 340); // matches mb-peer-popout duration
  };

  const doorsStartedAtRef = useRef(0);
  const playingFiredRef = useRef(false);

  useEffect(() => {
    doorsPhaseRef.current = doorsPhase;
  }, [doorsPhase]);

  // 4g: toggle body class so the parent's vol-bar can hide while the
  // doors are animating in/out. Cleared on every phase change to 'hidden'
  // (HLS playing) and on unmount.
  useEffect(() => {
    if (doorsPhase !== "hidden") {
      document.body.classList.add("mb-doors-active");
    } else {
      document.body.classList.remove("mb-doors-active");
    }
    return () => {
      document.body.classList.remove("mb-doors-active");
    };
  }, [doorsPhase]);

  useEffect(() => {
    cueRunnerRef.current = {
      cursor: 0,
      fired: 0,
      skipped: 0,
      cancelled: 0,
      history: [],
      lastShowId: null,
      lastShowPos: null,
    };
  }, []);

  // ===== HLS mount (3b + 4b + 3e + degrade ladder) =====
  useEffect(() => {
    const video = videoRef.current;
    if (!streamId || !video) return;

    let cancelled = false;
    networkRecoveryAttemptsRef.current = 0;
    currentFragRef.current = null;
    lastPlayheadAdvanceRef.current = Date.now();
    lastFragLoadAtRef.current = Date.now();

    // Degrade reset on a NEW stream only. remountCounter bumps re-run this
    // effect for both plain remounts AND degrades; we must NOT clear
    // degradedRef on those (it's the whole point that it persists). So we
    // key the reset off streamId changing, tracked via a ref.
    if (lastMountStreamIdRef.current !== streamId) {
      lastMountStreamIdRef.current = streamId;
      degradedRef.current = false;
    }

    // TARGET_HEIGHT is dynamic: a degraded TV targets 720 just like
    // Display. Non-degraded TV targets 1080. Display always 720.
    // FORCE_720: whole fleet targets 720 (see switch comment at top).
    const TARGET_HEIGHT = FORCE_720 ? 720 : degradedRef.current ? 720 : isTv ? 1080 : 720;

    const onTimeUpdate = () => {
      lastPlayheadAdvanceRef.current = Date.now();
      if (
        networkRecoveryAttemptsRef.current > 0 &&
        Date.now() - lastNetworkRecoveryAtRef.current > HEALTHY_PLAYBACK_RESET_MS
      ) {
        const prev = networkRecoveryAttemptsRef.current;
        networkRecoveryAttemptsRef.current = 0;
        console.log(
          `[beacon] watchdog: 30s healthy playback, recovery counter reset (was ${prev})`
        );
      }
    };
    video.addEventListener("timeupdate", onTimeUpdate);

    const onEnded = () => {
      if (cancelled) return;
      console.warn(
        `[beacon] video: ended event fired (duration=${video.duration?.toFixed(1) || "?"}s) — remounting hls`
      );
      // Graceful: keep the last frame + overlay rather than flashing black.
      softSwapRef.current = true;
      showRecoveringOverlay();
      setRemountCounter((c) => c + 1);
    };
    video.addEventListener("ended", onEnded);

    const fetchManifest = async () => {
      // FORCE_720: the whole fleet requests the 720 lane (isTv=0 → backend
      // serves res=720). The 1080 lane stays resolved server-side but no
      // customer requests it, so no fat segments are pulled for it. When
      // not forced: degraded requests 720, otherwise TV requests 1080.
      // streamId is unchanged either way — anchor + sync path untouched.
      const effectiveIsTv = FORCE_720 ? 0 : degradedRef.current ? 0 : isTv ? 1 : 0;
      const url =
        `${BEACON_API_BASE}/api/direct-manifest` +
        `?streamId=${encodeURIComponent(streamId)}` +
        `&isTv=${effectiveIsTv}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`manifest fetch ${r.status}`);
      const j = await r.json();
      let manifestUrl = j.proxyManifestUrl || j.directManifestUrl;
      if (!manifestUrl) throw new Error("no manifest url in response");
      if (manifestUrl.startsWith("/")) {
        manifestUrl = `${BEACON_API_BASE}${manifestUrl}`;
      }
      return { manifestUrl, source: j.proxyManifestUrl ? "proxy" : "direct" };
    };

    const mount = async () => {
      let manifest;
      try {
        manifest = await fetchManifest();
      } catch (e) {
        console.warn("[beacon] HLS manifest fetch failed:", e.message);
        return;
      }
      if (cancelled) return;
      const tag = remountCounter > 0 ? `remount #${remountCounter}` : "initial";
      const laneTag = degradedRef.current ? " [degraded→720]" : "";
      console.log(`[beacon] HLS mount (${tag})${laneTag}: manifest source = ${manifest.source}`);

      if (!Hls.isSupported()) {
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = manifest.manifestUrl;
          video.play().catch(() => {});
        } else {
          console.warn("[beacon] HLS not supported in this browser");
        }
        return;
      }

      const hls = new Hls({
        // TV needs more buffer headroom than Display — 1080p segments
        // are ~3x the bytes of 720p, so any single late segment is
        // more likely to choke playback. Bumping these gives TV ~4-8s
        // more buffer to absorb bursty delivery without stuttering.
        // Cue timing is PDT-anchored so the extra latency-behind-live
        // does NOT affect beacon sync — only visible content lag.
        liveSyncDurationCount: isTv ? 6 : 6,
        liveMaxLatencyDurationCount: isTv ? 12 : 12,
        backBufferLength: 30,
        // Buffer hygiene (2026-07-14): we deliberately sit ~30s behind the
        // live edge, which means ~30s of already-published segments exist
        // ahead of the playhead. maxBufferLength: 60 tells hls.js to
        // aggressively pre-download all of it — turning the positional
        // cushion into stored runway that silently absorbs 10-15s upstream
        // delivery stalls. lowLatencyMode OFF: it exists for people chasing
        // 3s latency; we're the opposite, and it biases buffers thinner.
        maxBufferLength: 60,
        enableWorker: true,
        lowLatencyMode: false,
        capLevelToPlayerSize: false,
        debug: false,
      });

      if (cancelled) {
        try { hls.destroy(); } catch {}
        return;
      }

      hlsRef.current = hls;
      hls.loadSource(manifest.manifestUrl);
      hls.attachMedia(video);
      video.play().catch(() => {});

      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        const levels = data.levels || [];
        const summary = levels
          .map(
            (l) =>
              `${l.height || "?"}p@${Math.round((l.bitrate || 0) / 1000)}kbps`
          )
          .join(" / ");
        let targetIdx = -1;
        for (let i = 0; i < levels.length; i++) {
          const h = levels[i].height || 0;
          if (h <= TARGET_HEIGHT && h > 0) {
            if (targetIdx === -1 || h > (levels[targetIdx].height || 0)) {
              targetIdx = i;
            }
          }
        }
        if (targetIdx === -1 && levels.length > 0) {
          targetIdx = 0;
          for (let i = 1; i < levels.length; i++) {
            if ((levels[i].height || 0) < (levels[targetIdx].height || 0)) {
              targetIdx = i;
            }
          }
        }
        if (targetIdx >= 0) {
          hls.currentLevel = targetIdx;
          const picked = levels[targetIdx].height || "?";
          console.log(
            `[beacon] HLS parsed: ${levels.length} levels [${summary}] → locked ${picked}p`
          );
        }
      });

      hls.on(Hls.Events.FRAG_CHANGED, (_, data) => {
        const frag = data?.frag;
        if (!frag) return;
        if (frag.programDateTime == null) return;
        currentFragRef.current = {
          start: frag.start,
          duration: frag.duration,
          programDateTime: frag.programDateTime,
        };
      });

      hls.on(Hls.Events.FRAG_LOADED, () => {
        lastFragLoadAtRef.current = Date.now();
        // Video is pulling segments again — if a recovery overlay was up
        // (degrade / graceful remount), fade it out now that we're live.
        clearRecoveringOverlay();
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        console.warn(`[beacon] HLS fatal — ${data.type}/${data.details}`);

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (networkRecoveryAttemptsRef.current >= 5) {
            console.warn(
              "[beacon] HLS: 5 consecutive network recoveries failed — giving up"
            );
            return;
          }
          networkRecoveryAttemptsRef.current++;
          lastNetworkRecoveryAtRef.current = Date.now();
          console.log(
            `[beacon] HLS: network recovery ${networkRecoveryAttemptsRef.current}/5 — refetching manifest`
          );
          (async () => {
            try {
              const fresh = await fetchManifest();
              hls.loadSource(fresh.manifestUrl);
              hls.startLoad();
            } catch (e) {
              console.warn(`[beacon] HLS refetch failed: ${e.message}`);
              try { hls.startLoad(); } catch {}
            }
          })();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          console.log("[beacon] HLS: attempting media recovery");
          try { hls.recoverMediaError(); } catch {}
        }
      });
    };

    mount();

    return () => {
      cancelled = true;
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      const hls = hlsRef.current;
      if (hls) {
        try { hls.destroy(); } catch {}
        hlsRef.current = null;
      }
      // Held-frame swap: when softSwapRef is set (a degrade or graceful
      // remount that should NOT flash black), skip the hard video clear so
      // the last painted frame persists under the "Optimizing…" overlay
      // until the new lane paints. Consume the flag here. On a true unmount
      // / streamId change, softSwapRef is false → normal hard clear.
      if (softSwapRef.current) {
        softSwapRef.current = false;
        // intentionally NOT pausing / clearing src — keep last frame.
      } else {
        try {
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch {}
      }
      networkRecoveryAttemptsRef.current = 0;
      currentFragRef.current = null;
    };
  }, [streamId, isTv, remountCounter]);

  // ===== 4g: apply volume continuously =====
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof hlsVolume !== "number") return;
    video.volume = Math.max(0, Math.min(1, hlsVolume / 100));
  }, [hlsVolume]);

  // ===== Doors lifecycle timers (3c) =====
  useEffect(() => {
    doorsStartedAtRef.current = Date.now();
    playingFiredRef.current = false;
    setDoorsPhase("closing");
    setShowPatience(false);

    const patienceTimer = setTimeout(() => {
      if (playingFiredRef.current) return;
      setShowPatience(true);
      console.log("[beacon] doors: 15s — showing patience message");
    }, DOORS_PATIENCE_MS);

    const errorTimer = setTimeout(() => {
      if (playingFiredRef.current) return;
      console.warn(
        "[beacon] doors: 30s hard cap reached without playing event"
      );
      setShowPatience(false);
      setDoorsPhase("error-flashing");
    }, DOORS_MAX_MS);

    return () => {
      clearTimeout(patienceTimer);
      clearTimeout(errorTimer);
    };
    // doorsAttempt: "Try Again" on the error card re-runs this whole
    // lifecycle (phase back to closing, fresh patience/error timers).
  }, [doorsAttempt]);

  // ===== Doors BLE wake loop (4a) =====
  // (doorsAttempt in deps: the retry path restarts the wake loop, since
  //  the error transition had cleared it.)
  useEffect(() => {
    if (!deviceId || !sendBeaconCmd || !sendCancel) return;

    console.log("[beacon] doors BLE: t=0 CANCEL (clearing in-flight effect)");
    sendCancel(deviceId);

    doorsWakeIntervalRef.current = setInterval(() => {
      console.log("[beacon] doors BLE: WAKE_UP_FAST");
      sendBeaconCmd(deviceId, "WAKE_UP_FAST", "OFF");
    }, DOORS_WAKE_MS);

    return () => {
      if (doorsWakeIntervalRef.current) {
        clearInterval(doorsWakeIntervalRef.current);
        doorsWakeIntervalRef.current = null;
      }
    };
  }, [deviceId, sendBeaconCmd, sendCancel, doorsAttempt]);

  // Video 'playing' event listener.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlaying = () => {
      // Recovery: a degrade/remount that resumed playback should drop the
      // "Optimizing…" overlay even if FRAG_LOADED hasn't fired yet.
      clearRecoveringOverlay();
      if (playingFiredRef.current) return;
      if (doorsPhase === "error-flashing" || doorsPhase === "error") {
        console.log("[beacon] doors: playing fired LATE after error — ignoring");
        playingFiredRef.current = true;
        return;
      }
      playingFiredRef.current = true;
      setShowPatience(false);

      const elapsed = Date.now() - doorsStartedAtRef.current;
      const wait = Math.max(0, DOORS_MIN_MS - elapsed);
      console.log(
        `[beacon] doors: playing fired at +${elapsed}ms — revealing in ${wait}ms`
      );

      setTimeout(() => {
        if (!videoRef.current) return;
        setDoorsPhase("ready");
      }, wait);
    };

    video.addEventListener("playing", onPlaying);
    return () => video.removeEventListener("playing", onPlaying);
  }, [doorsPhase]);

  // ===== computeSyncShowPos (4b) =====
  const computeSyncShowPos = () => {
    const a = anchorRef.current;
    const cf = currentFragRef.current;
    const v = videoRef.current;
    if (!a || !cf || !v) return null;
    const elapsedInFrag = Math.max(0, v.currentTime - cf.start);
    const playPDTMs = cf.programDateTime + elapsedInFrag * 1000;
    return a.anchorShowPosSec + (playPDTMs - a.anchorPDTMs) / 1000;
  };

  // ===== Preshow handoff (4c) =====
  const teardownPreshowHandoff = () => {
    const h = preshowHandoffRef.current;
    if (h.wakeIntervalId) { clearInterval(h.wakeIntervalId); h.wakeIntervalId = null; }
    if (h.firstWakeTimerId) { clearTimeout(h.firstWakeTimerId); h.firstWakeTimerId = null; }
    if (h.cancelTimerId) { clearTimeout(h.cancelTimerId); h.cancelTimerId = null; }
    h.active = false;
    h.startedAt = 0;
    h.wakeCount = 0;
    h.lastWakeAt = 0;
    h.cancelScheduledFor = 0;
  };

  const fireHandoffWake = () => {
    const h = preshowHandoffRef.current;
    if (h.cancelScheduledFor > 0) {
      const msUntilCancel = h.cancelScheduledFor - Date.now();
      if (msUntilCancel < PRESHOW_HANDOFF_WAKE_TO_CANCEL_GAP_MS) {
        console.log(
          `[beacon] handoff: skipped wake (${msUntilCancel}ms until scheduled CANCEL)`
        );
        return;
      }
    }
    const did = deviceIdRef.current;
    const fn = sendBeaconCmdRef.current;
    if (did && fn) fn(did, "WAKE_UP_FAST", "OFF");
    h.lastWakeAt = Date.now();
    h.wakeCount += 1;
    console.log(`[beacon] handoff: wake #${h.wakeCount} fired`);
  };

  const fireFinalHandoffCancel = () => {
    const h = preshowHandoffRef.current;
    if (!h.active) return;
    if (h.wakeIntervalId) { clearInterval(h.wakeIntervalId); h.wakeIntervalId = null; }
    if (h.firstWakeTimerId) { clearTimeout(h.firstWakeTimerId); h.firstWakeTimerId = null; }
    const did = deviceIdRef.current;
    const cf = sendCancelRef.current;
    if (did && cf) cf(did);
    h.active = false;
    h.cancelTimerId = null;
    h.cancelScheduledFor = 0;
    console.log("[beacon] handoff: final CANCEL fired, cue runner takes over");
  };

  const tryScheduleHandoffCancel = () => {
    const h = preshowHandoffRef.current;
    if (!h.active) return;
    if (!anchorRef.current) return;
    const cuesNow = cuesRef.current;
    if (cuesNow.length === 0) return;

    const showPos = computeSyncShowPos();
    if (showPos == null) return;
    const showPosMs = showPos * 1000;

    let targetIdx = -1;
    for (let i = 0; i < cuesNow.length; i++) {
      const c = cuesNow[i];
      if (c.tMs <= showPosMs) continue;
      const msUntilCue = c.tMs - showPosMs;
      const cancelInMs = msUntilCue - PRESHOW_HANDOFF_CANCEL_TO_CUE_GAP_MS;
      if (cancelInMs < 0) continue;
      const cancelAtWallMs = Date.now() + cancelInMs;
      if (
        h.lastWakeAt > 0 &&
        cancelAtWallMs - h.lastWakeAt < PRESHOW_HANDOFF_WAKE_TO_CANCEL_GAP_MS
      ) {
        continue;
      }
      targetIdx = i;
      break;
    }

    if (targetIdx < 0) {
      const lastCueMs = cuesNow[cuesNow.length - 1].tMs;
      if (h.wakeCount >= 1 && showPosMs > lastCueMs) {
        console.log("[beacon] handoff: no remaining cues in show — final CANCEL");
        if (h.wakeIntervalId) { clearInterval(h.wakeIntervalId); h.wakeIntervalId = null; }
        const did = deviceIdRef.current;
        const cf = sendCancelRef.current;
        if (did && cf) cf(did);
        h.active = false;
      }
      return;
    }

    const target = cuesNow[targetIdx];
    const msUntilCue = target.tMs - showPosMs;
    const cancelInMs = msUntilCue - PRESHOW_HANDOFF_CANCEL_TO_CUE_GAP_MS;
    const cancelAtWallMs = Date.now() + cancelInMs;

    if (Math.abs(h.cancelScheduledFor - cancelAtWallMs) < 500) return;

    if (h.cancelTimerId) { clearTimeout(h.cancelTimerId); h.cancelTimerId = null; }
    h.cancelScheduledFor = cancelAtWallMs;
    h.cancelTimerId = setTimeout(fireFinalHandoffCancel, cancelInMs);
    console.log(
      `[beacon] handoff: scheduled CANCEL in ${cancelInMs}ms (next cue at showPos ${(target.tMs / 1000).toFixed(2)}s: ${target.fx})`
    );
  };

  const handoffWakeTick = () => {
    const h = preshowHandoffRef.current;
    if (!h.active) return;
    if (Date.now() - h.startedAt > PRESHOW_HANDOFF_MAX_DURATION_MS) {
      console.warn(
        `[beacon] handoff: max duration reached (${h.wakeCount} wakes, no lock) — final CANCEL, giving up`
      );
      if (h.wakeIntervalId) { clearInterval(h.wakeIntervalId); h.wakeIntervalId = null; }
      const did = deviceIdRef.current;
      const cf = sendCancelRef.current;
      if (did && cf) cf(did);
      h.active = false;
      return;
    }
    fireHandoffWake();
    tryScheduleHandoffCancel();
  };

  const startPreshowHandoff = (trigger) => {
    const h = preshowHandoffRef.current;
    if (h.active) {
      console.log(
        `[beacon] handoff: already active, ignoring duplicate trigger (${trigger})`
      );
      return;
    }
    h.active = true;
    h.trigger = trigger;
    h.startedAt = Date.now();
    h.wakeCount = 0;
    h.lastWakeAt = 0;
    h.cancelScheduledFor = 0;

    console.log(`[beacon] handoff: started (${trigger})`);

    if (trigger === "showtime") {
      const did = deviceIdRef.current;
      const cf = sendCancelRef.current;
      if (did && cf) cf(did);
      h.firstWakeTimerId = setTimeout(() => {
        h.firstWakeTimerId = null;
        fireHandoffWake();
        h.wakeIntervalId = setInterval(
          handoffWakeTick,
          PRESHOW_HANDOFF_WAKE_INTERVAL_MS
        );
      }, PRESHOW_HANDOFF_FIRST_WAKE_DELAY_MS);
    } else {
      fireHandoffWake();
      h.wakeIntervalId = setInterval(
        handoffWakeTick,
        PRESHOW_HANDOFF_WAKE_INTERVAL_MS
      );
    }

    tryScheduleHandoffCancel();
  };

  // Reveal sequence + doors-handoff BLE on phase transitions.
  useEffect(() => {
    if (doorsPhase === "ready") {
      if (doorsWakeIntervalRef.current) {
        clearInterval(doorsWakeIntervalRef.current);
        doorsWakeIntervalRef.current = null;
      }
      if (deviceId && sendCancel) {
        console.log("[beacon] doors BLE: handoff (success) — CANCEL");
        sendCancel(deviceId);
      }

      const chime = chimeRef.current;
      if (chime) {
        try {
          chime.volume = 0.15;
          chime.currentTime = 0;
          chime.play().catch(() => {});
        } catch {}
      }
      console.log("[beacon] doors: green flash + chime, dissolving in 1s");
      const t = setTimeout(() => setDoorsPhase("revealed"), DOORS_GREEN_HOLD_MS);

      // 4c-fix: schedule the late-lock handoff trigger in a ref so it
      // survives the upcoming doorsPhase transitions ('ready' → 'revealed'
      // → 'hidden'). Without this, the effect cleanup below would clear
      // the timer before it ever fires. Multitest2 line 2562.
      if (preshowTriggerTimerRef.current) {
        clearTimeout(preshowTriggerTimerRef.current);
      }
      preshowTriggerTimerRef.current = setTimeout(() => {
        preshowTriggerTimerRef.current = null;
        if (anchorRef.current) {
          startPreshowHandoff("late-lock");
        }
      }, PRESHOW_HANDOFF_FIRST_WAKE_DELAY_MS);

      return () => {
        clearTimeout(t);
      };
    }
    if (doorsPhase === "revealed") {
      const t = setTimeout(() => {
        setDoorsPhase("hidden");
        console.log("[beacon] doors: handoff complete");
      }, DOORS_DISSOLVE_MS);
      return () => clearTimeout(t);
    }
    if (doorsPhase === "error-flashing") {
      if (doorsWakeIntervalRef.current) {
        clearInterval(doorsWakeIntervalRef.current);
        doorsWakeIntervalRef.current = null;
      }
      if (deviceId && sendCancel) {
        console.log("[beacon] doors BLE: handoff (error) — CANCEL");
        sendCancel(deviceId);
      }
      // 4c-fix: cancel the late-lock trigger if doors errored out.
      if (preshowTriggerTimerRef.current) {
        clearTimeout(preshowTriggerTimerRef.current);
        preshowTriggerTimerRef.current = null;
      }
      teardownPreshowHandoff();

      console.warn("[beacon] doors: blue pulse — error card in 3s");
      const t = setTimeout(() => setDoorsPhase("error"), DOORS_ERROR_FLASH_MS);
      return () => clearTimeout(t);
    }
  }, [doorsPhase, deviceId, sendCancel]);

  useEffect(() => {
    if (preshowHandoffRef.current.active) {
      tryScheduleHandoffCancel();
    }
  }, [cues, anchor]);

  useEffect(() => {
    return () => {
      // 4c-fix: cancel the late-lock trigger on unmount too.
      if (preshowTriggerTimerRef.current) {
        clearTimeout(preshowTriggerTimerRef.current);
        preshowTriggerTimerRef.current = null;
      }
      // 5b: clear any pending peer-bubble pop-out timer.
      if (peerHideTimerRef.current) {
        clearTimeout(peerHideTimerRef.current);
        peerHideTimerRef.current = null;
      }
      // 5c: clear any pending warning-shake timer.
      if (warnShakeTimerRef.current) {
        clearTimeout(warnShakeTimerRef.current);
        warnShakeTimerRef.current = null;
      }
      // degrade: clear any pending recovery-overlay fade timer.
      if (recoverFadeTimerRef.current) {
        clearTimeout(recoverFadeTimerRef.current);
        recoverFadeTimerRef.current = null;
      }
      // take-control: clear all badge lifecycle timers.
      ctrlTimersRef.current.forEach((t) => clearTimeout(t));
      ctrlTimersRef.current = [];
      teardownPreshowHandoff();
    };
  }, []);

  // ===== Mute coordination (3c-fix + 4g) =====
  // Doors forces muted=true while closing/error. When doors hit
  // 'revealed', wait 500ms then apply the USER'S hlsMuted preference
  // (rather than always unmuting). After 'hidden', mute changes from
  // the user propagate live.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (doorsPhase === "revealed") {
      const t = setTimeout(() => {
        if (videoRef.current) videoRef.current.muted = !!hlsMuted;
      }, 500);
      return () => clearTimeout(t);
    }
    if (doorsPhase === "hidden") {
      // 4g: live mute changes after doors finished.
      video.muted = !!hlsMuted;
      return;
    }
    // closing / ready / error — keep muted for the doors animation.
    video.muted = true;
  }, [doorsPhase, hlsMuted]);

  // ===== Cue runner (4d) =====
  const sendBleCue = (cue) => {
    console.log(`[beacon] cue FIRE: ${cue.raw}`);
    const did = deviceIdRef.current;
    const fn = sendBeaconCmdRef.current;
    if (did && fn) fn(did, cue.fx, cue.vib || "OFF");
  };

  const sendBleCancelLocal = (reason) => {
    cueRunnerRef.current.cancelled += 1;
    console.log(`[beacon] cue CANCEL: ${reason}`);
    const did = deviceIdRef.current;
    const cf = sendCancelRef.current;
    if (did && cf) cf(did);
  };

  const pushCueHistory = (entry) => {
    const h = cueRunnerRef.current.history;
    h.push(entry);
    if (h.length > 30) h.splice(0, h.length - 30);
  };

  const tickCueRunner = () => {
    if (graceModeActiveRef.current) return;
    if (doorsPhaseRef.current !== "hidden") return;
    if (preshowHandoffRef.current.active) return;

    const a = anchorRef.current;
    if (!a) return;
    const cuesNow = cuesRef.current;

    if (cueRunnerRef.current.lastShowId !== a.showId) {
      cueRunnerRef.current.cursor = 0;
      cueRunnerRef.current.fired = 0;
      cueRunnerRef.current.skipped = 0;
      cueRunnerRef.current.cancelled = 0;
      cueRunnerRef.current.lastShowId = a.showId;
      cueRunnerRef.current.lastShowPos = null;
    }

    const showPos = computeSyncShowPos();
    if (showPos == null) return;
    const showPosMs = showPos * 1000;
    const lastPos = cueRunnerRef.current.lastShowPos;

    if (lastPos != null) {
      const deltaMs = showPosMs - lastPos * 1000;
      if (Math.abs(deltaMs - TICK_MS) > DRIFT_THRESHOLD_MS) {
        if (deltaMs > 0) {
          sendBleCancelLocal(`forward jump ${(deltaMs / 1000).toFixed(1)}s`);
          let skipped = 0;
          while (
            cueRunnerRef.current.cursor < cuesNow.length &&
            cuesNow[cueRunnerRef.current.cursor].tMs <
              showPosMs - FORWARD_TOLERANCE_MS
          ) {
            const c = cuesNow[cueRunnerRef.current.cursor];
            pushCueHistory({
              tMs: c.tMs,
              fx: c.fx,
              action: "skip-jump",
              at: Date.now(),
            });
            cueRunnerRef.current.cursor += 1;
            skipped += 1;
          }
          if (skipped > 0) {
            cueRunnerRef.current.skipped += skipped;
            console.log(
              `[beacon] cue: skipped ${skipped} stale cue(s) from forward jump`
            );
          }
        } else {
          sendBleCancelLocal(`backward jump ${(deltaMs / 1000).toFixed(1)}s`);
        }
      }
    }
    cueRunnerRef.current.lastShowPos = showPos;
    tinkShowPosRef.current = showPos;

    if (onShowPosTickRef.current) onShowPosTickRef.current(showPos);

    while (cueRunnerRef.current.cursor < cuesNow.length) {
      const cue = cuesNow[cueRunnerRef.current.cursor];
      if (cue.tMs > showPosMs) break;
      const lateBy = showPosMs - cue.tMs;
      if (lateBy <= FORWARD_TOLERANCE_MS) {
        sendBleCue(cue);
        cueRunnerRef.current.fired += 1;
        pushCueHistory({
          tMs: cue.tMs,
          fx: cue.fx,
          action: "fire",
          at: Date.now(),
          lateBy,
        });
      } else {
        cueRunnerRef.current.skipped += 1;
        pushCueHistory({
          tMs: cue.tMs,
          fx: cue.fx,
          action: "skip-late",
          at: Date.now(),
          lateBy,
        });
        console.log(
          `[beacon] cue: skip ${cue.fx} @ ${(cue.tMs / 1000).toFixed(1)}s (was ${(lateBy / 1000).toFixed(1)}s late)`
        );
      }
      cueRunnerRef.current.cursor += 1;
    }
  };

  useEffect(() => {
    const id = setInterval(tickCueRunner, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ===== Degrade-ladder recovery helpers =====
  //
  // These run when the frag-idle clock crosses a rung. They escalate from
  // cheapest (un-stick in place) to most disruptive (graceful remount),
  // and none of them flash black — rungs 2/3 hold the last frame under the
  // "Optimizing…" overlay until the new lane paints.

  // Rung 1: quiet recovery. Re-issue hls.startLoad() to un-stick the
  // existing player, and if the playhead is sitting in a buffered gap,
  // hop the MINIMUM distance forward to the nearest healthy buffered
  // range — but only if that hop is small (≤ MICRO_HOP_MAX_S) so it stays
  // under the cue runner's forward-jump guard. NEVER seek to live edge
  // (that would throw away the ~30s cushion and trip the guard). If the
  // only healthy range is far ahead, do NOT hop — let the clock escalate
  // to the 720 degrade instead. Throttled so we don't hammer startLoad.
  const tryQuietRecovery = () => {
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls) return;

    const now = Date.now();
    if (now - lastQuietRecoveryAtRef.current < QUIET_RECOVERY_COOLDOWN_MS) {
      return; // already nudged recently; let time escalate the rung
    }
    lastQuietRecoveryAtRef.current = now;

    // Bounded micro-hop: is the playhead in a gap with a healthy range
    // just ahead?
    try {
      const ct = video.currentTime;
      const buffered = video.buffered;
      let inBuffered = false;
      let nextRangeStart = null;
      for (let i = 0; i < buffered.length; i++) {
        const s = buffered.start(i);
        const e = buffered.end(i);
        if (ct >= s - 0.1 && ct <= e) {
          inBuffered = true;
          break;
        }
        if (s > ct) {
          if (nextRangeStart == null || s < nextRangeStart) nextRangeStart = s;
        }
      }
      if (!inBuffered && nextRangeStart != null) {
        const hop = nextRangeStart - ct;
        if (hop > 0 && hop <= MICRO_HOP_MAX_S) {
          video.currentTime = nextRangeStart + 0.05;
          console.log(
            `[beacon] watchdog: quiet micro-hop ${hop.toFixed(2)}s to healthy buffer`
          );
        } else {
          console.log(
            `[beacon] watchdog: gap ahead too large (${hop.toFixed(1)}s) — not hopping, will escalate if needed`
          );
        }
      }
    } catch (e) {
      // buffered access can throw on some states; ignore and just reload
    }

    console.log("[beacon] watchdog: quiet recovery — startLoad()");
    try {
      hls.startLoad();
      video.play().catch(() => {});
    } catch (e) {
      console.warn(`[beacon] watchdog: quiet recovery threw — ${e.message}`);
    }
  };

  // Rung 2: degrade to 720 (TV). Pin degradedRef so the re-mount fetches
  // the 720 lane, show the held-frame overlay, and bump remountCounter to
  // re-run the mount effect. softSwapRef preserves the last frame (no
  // black). One-way: degradedRef stays set for the rest of the stream.
  //
  // On Display (already 720) there's no lower lane to drop to, so this is
  // effectively a "graceful remount of 720" — still under the held frame +
  // overlay, which is strictly better than the old black remount.
  const degradeOrRemount720 = () => {
    if (!FORCE_720 && isTv && !degradedRef.current) {
      console.warn(
        "[beacon] watchdog: stall persists — degrading TV 1080 → 720 (held frame)"
      );
      degradedRef.current = true;
    } else {
      console.warn(
        "[beacon] watchdog: stall persists — graceful remount (held frame)"
      );
    }
    softSwapRef.current = true;
    showRecoveringOverlay();
    lastFragLoadAtRef.current = Date.now();
    lastPlayheadAdvanceRef.current = Date.now();
    setRemountCounter((c) => c + 1);
  };

  // ===== Watchdog (3e + stall ladder, 2-rung since FORCE_720) =====
  const syncWatchdog = () => {
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls) return;

    const idleMs = Date.now() - lastPlayheadAdvanceRef.current;
    const fragIdleMs = Date.now() - lastFragLoadAtRef.current;

    // Buffered runway ahead of the playhead — the real health signal in
    // the deep-buffer world. Computed once, used by the whole ladder.
    let runwayS = 0;
    try {
      const ct = video.currentTime;
      const b = video.buffered;
      for (let i = 0; i < b.length; i++) {
        if (ct >= b.start(i) - 0.1 && ct <= b.end(i)) {
          runwayS = b.end(i) - ct;
          break;
        }
      }
    } catch {}

    // ----- Frag-idle escalation ladder -----
    // Deep-buffer awareness (2026-07-14): with maxBufferLength 60 the
    // player fills to the live edge and then STOPS fetching by design —
    // frag-idle with plentiful runway is a HEALTHY full buffer, not a
    // stall. Only act when runway is actually shrinking low.
    if (fragIdleMs > FRAG_IDLE_THRESHOLD_MS) {
      const RUNWAY_HEALTHY_S = 12;
      if (runwayS >= RUNWAY_HEALTHY_S) {
        return; // buffer full, player idling on purpose — nothing to do
      }
      if (fragIdleMs >= FRAG_IDLE_REMOUNT_MS) {
        // Rung 2 (~14s): sustained stall AND runway nearly dry → visible
        // rescue. Until dry, keep quiet-recovering — segments may resume
        // and the customer never sees a thing.
        const RUNWAY_DRY_S = 6;
        if (runwayS >= RUNWAY_DRY_S) {
          console.warn(
            `[beacon] watchdog: stall ${(fragIdleMs / 1000).toFixed(0)}s but ${runwayS.toFixed(0)}s runway left — holding off remount, quiet-recovering`
          );
          tryQuietRecovery();
          return;
        }
        // (Becomes the 1080→720 degrade on TV if FORCE_720 is ever off.)
        degradeOrRemount720();
        return;
      }
      // Rung 1 (~6s idle + runway thinning): quiet, in-place recovery.
      // No visible change, no overlay.
      console.warn(
        `[beacon] watchdog: no segments in ${(fragIdleMs / 1000).toFixed(0)}s, runway ${runwayS.toFixed(0)}s — quiet recovery (rung 1)`
      );
      tryQuietRecovery();
      return;
    }

    const dur = video.duration;
    const wedged =
      Number.isFinite(dur) &&
      dur > 0 &&
      video.currentTime >= dur - EOS_WEDGE_TOLERANCE_S;
    if (wedged) {
      // Require the wedge condition to persist for 3 seconds before
      // remounting. During normal live-edge play, currentTime can
      // briefly equal duration in the instant before a new segment
      // arrives — that's NOT a real wedge. Only sustained wedge
      // (stream actually ended OR pipeline stalled at the boundary)
      // warrants a remount — and now under the held frame + overlay.
      if (eosWedgeStartedAtRef.current == null) {
        eosWedgeStartedAtRef.current = Date.now();
      } else if (Date.now() - eosWedgeStartedAtRef.current >= 3000) {
        console.warn(
          `[beacon] watchdog: end-of-stream wedge sustained 3s+ (${dur.toFixed(1)}s) — graceful remount`
        );
        eosWedgeStartedAtRef.current = null;
        softSwapRef.current = true;
        showRecoveringOverlay();
        lastPlayheadAdvanceRef.current = Date.now();
        setRemountCounter((c) => c + 1);
        return;
      }
    } else if (eosWedgeStartedAtRef.current != null) {
      // Condition cleared before 3s — reset the timer so the next
      // wedge episode starts counting fresh.
      eosWedgeStartedAtRef.current = null;
    }

    if (video.paused && idleMs > PAUSE_IDLE_THRESHOLD_MS) {
      console.log("[beacon] watchdog: video paused, calling play()");
      video.play().catch((e) => {
        console.warn(`[beacon] watchdog: play() rejected — ${e.message}`);
      });
      lastPlayheadAdvanceRef.current = Date.now();
      return;
    }

    if (!video.paused && idleMs > PLAYHEAD_REMOUNT_THRESHOLD_MS) {
      console.warn(
        `[beacon] watchdog: stuck ${(idleMs / 1000).toFixed(0)}s after nudge — graceful remount`
      );
      softSwapRef.current = true;
      showRecoveringOverlay();
      lastPlayheadAdvanceRef.current = Date.now();
      setRemountCounter((c) => c + 1);
      return;
    }

    if (!video.paused && idleMs > PLAYHEAD_NUDGE_THRESHOLD_MS) {
      console.warn(
        `[beacon] watchdog: playhead stuck ${(idleMs / 1000).toFixed(0)}s, nudging`
      );
      try {
        if (hls.liveSyncPosition != null) {
          const behind = hls.liveSyncPosition - video.currentTime;
          if (behind > LIVE_EDGE_BEHIND_THRESHOLD_S) {
            video.currentTime = hls.liveSyncPosition;
            console.log(
              `[beacon] watchdog: seeked to live edge (was ${behind.toFixed(1)}s behind)`
            );
          }
        }
        hls.startLoad();
        video.play().catch(() => {});
      } catch (e) {
        console.warn(`[beacon] watchdog: nudge threw — ${e.message}`);
      }
      lastPlayheadAdvanceRef.current = Date.now();
    }
  };

  useEffect(() => {
    const id = setInterval(syncWatchdog, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // (Manual resolution toggle removed 2026-07-14 — fleet is FORCE_720.
  //  UP on the remote is a dead key again. If 1080 ever returns, restore
  //  the requestRes helper + UP-key effect + pill from backup history.)

  // ===== Quick-swap trigger =====
  // Fires when this stream has needed rescue twice this session. Checks
  // for a healthy same-show peer (findLockedPeer skips peers with bad
  // segHealth) and, if one exists, raises the offer overlay. Gated on:
  // doors finished (never over the entry animation), an anchor present
  // (we're mid-show, so "same show" is meaningful), not already answered,
  // and the peer not previously dismissed in this session.
  useEffect(() => {
    if (remountCounter < 2) return;
    if (quickSwapAnsweredRef.current) return;
    if (quickSwapOffer) return;
    if (doorsPhaseRef.current !== "hidden") return;
    const a = anchorRef.current;
    const showId = a?.showId || earlyEntryShowIdRef.current;
    if (!showId) return;
    const fn = findLockedPeerRef.current;
    const peer = fn ? fn(showId, streamIdRef.current) : null;
    if (!peer) return;
    if (dismissedPeersRef.current.has(peer.streamId)) return;
    console.log(
      `[beacon] quick-swap: stream needed ${remountCounter} rescues — offering healthy peer @${peer.name}`
    );
    setQuickSwapOffer(peer);
  }, [remountCounter, quickSwapOffer]);

  // ===== 4e: early-entry badge state machine =====

  useEffect(() => {
    if (!earlyEntry) return;
    if (!anchor) return;
    if (
      badgePhaseRef.current === "locked" ||
      badgePhaseRef.current === "exiting" ||
      badgePhaseRef.current === "hidden"
    ) {
      if (badgePhaseRef.current === "hidden") {
        if (lockChimeFiredRef.current) return;
      } else {
        return;
      }
    }

    console.log(
      `[beacon] early-entry: lock arrived (show=${anchor.showId})`
    );
    if (!lockChimeFiredRef.current) {
      try {
        const chime = connectionChimeRef.current;
        if (chime) {
          chime.volume = 0.5;
          chime.currentTime = 0;
          chime.play().catch(() => {});
        }
      } catch {}
      lockChimeFiredRef.current = true;
    }
    if (preshowHandoffRef.current.active) {
      tryScheduleHandoffCancel();
    }
    // Hide the peer-offer bubble if it was up — own stream just locked,
    // no longer offering a handoff (multitest2 line 5023). Uses the
    // existing pop-out animation via maybeHidePeerBubble so it slides
    // off in sync with the locked-state celebration.
    maybeHidePeerBubble();
    setBadgePhase("locked");
  }, [anchor, earlyEntry]);

  useEffect(() => {
    if (badgePhase === "locked") {
      const t = setTimeout(
        () => setBadgePhase("exiting"),
        SF_EARLY_LOCKED_HOLD_MS
      );
      return () => clearTimeout(t);
    }
    if (badgePhase === "exiting") {
      const t = setTimeout(
        () => setBadgePhase("hidden"),
        SF_EARLY_EXIT_ANIM_MS
      );
      return () => clearTimeout(t);
    }
  }, [badgePhase]);

  useEffect(() => {
    if (!earlyEntry) {
      setBadgePhase("hidden");
      setBadgeText(null);
      syncingStartedAtRef.current = 0;
      return;
    }

    const tick = () => {
      const p = badgePhaseRef.current;
      if (p === "locked" || p === "exiting") return;
      if (p === "hidden" && lockChimeFiredRef.current) return;

      const showId = earlyEntryShowIdRef.current;
      const showtime = showtimeMsRef.current;
      if (!showId || showtime == null) return;

      const now = Date.now();
      const phaseNow = now - MB_COUNTDOWN_DISPLAY_OFFSET_MS;
      const untilStartMs = showtime - phaseNow;
      const showName = prettyShow(showId);

      if (untilStartMs > 0) {
        const isUrgent = untilStartMs <= MB_COUNTDOWN_URGENT_THRESHOLD_MS;
        setBadgePhase("countdown");
        setBadgeText({
          showName,
          detail: "starts in",
          time: formatCountdown(untilStartMs),
          urgent: isUrgent,
        });
        // 5b: no peer offer during the pre-showtime countdown — only in
        // the syncing wait. Clear if it was showing.
        maybeHidePeerBubble();
        return;
      }

      if (!syncingStartedAtRef.current) {
        syncingStartedAtRef.current = now;
        if (!preshowHandoffRef.current.active) {
          console.log("[beacon] early-entry: showtime crossed, starting Path 1 handoff");
          startPreshowHandoff("showtime");
        }
      }
      const phases = getSyncingPhases(showId);
      const elapsed = now - syncingStartedAtRef.current;
      let detailText;
      let remaining;
      if (elapsed < phases.initialMs) {
        detailText = "Syncing to show";
        remaining = phases.initialMs - elapsed;
      } else {
        const extendedElapsed = elapsed - phases.initialMs;
        const roundElapsed = extendedElapsed % phases.extendedMs;
        detailText = "Taking a moment longer...";
        remaining = phases.extendedMs - roundElapsed;
      }
      setBadgePhase("syncing");
      setBadgeText({
        showName: `${showName}:`,
        detail: detailText,
        time: formatCountdown(remaining),
        urgent: false,
      });

      // 5b: peer-offer bubble. We're in the syncing wait — if another
      // streamer is already locked on this same show, offer the handoff.
      // Hold it until doors have finished (never show over the doors run).
      const doorsRunning = doorsPhaseRef.current && doorsPhaseRef.current !== "hidden";
      const fnPeer = findLockedPeerRef.current;
      let peer = doorsRunning
        ? null
        : fnPeer
        ? fnPeer(showId, streamIdRef.current)
        : null;
      // Respect prior "NO, STAY" — if user dismissed this peer this
      // session, don't re-offer it even if it's still locked on the show.
      if (peer && dismissedPeersRef.current.has(peer.streamId)) {
        peer = null;
      }
      if (peer) {
        if (!peerBubbleShownRef.current) {
          peerBubbleShownRef.current = true;
          setPeerBubble({ streamId: peer.streamId, exiting: false });
          // one-shot pop sound on first appearance
          try {
            if (peerPopRef.current) {
              peerPopRef.current.volume = 0.7;
              peerPopRef.current.currentTime = 0;
              peerPopRef.current.play().catch(() => {});
            }
          } catch {}
        } else {
          // already shown — keep target fresh, no re-pop
          setPeerBubble((prev) =>
            prev.streamId === peer.streamId
              ? prev
              : { streamId: peer.streamId, exiting: false }
          );
        }
      } else {
        maybeHidePeerBubble();
      }
    };

    tick();
    const id = setInterval(tick, BADGE_TICK_MS);
    return () => clearInterval(id);
  }, [earlyEntry]);

  useEffect(() => {
    // 5b: peer offer only lives during the syncing wait. The moment the
    // badge flips to locked / exiting / hidden, retire the bubble.
    if (
      badgePhase === "locked" ||
      badgePhase === "exiting" ||
      badgePhase === "hidden"
    ) {
      maybeHidePeerBubble();
    }
  }, [badgePhase]);

  useEffect(() => {
    if (badgePhase === "locked") {
      const a = anchorRef.current;
      const showId = a?.showId || earlyEntryShowIdRef.current;
      const showName = prettyShow(showId);
      setBadgeText({
        showName: `${showName}:`,
        detail: "Connected! Enjoy the show!",
        time: null,
        urgent: false,
      });
    }
  }, [badgePhase]);

  // Console debug helpers.
  useEffect(() => {
    window.beaconSyncPos = () => computeSyncShowPos();
    window.beaconHandoff = () => ({ ...preshowHandoffRef.current });
    window.beaconCueRunner = () => ({
      ...cueRunnerRef.current,
      history: [...cueRunnerRef.current.history],
    });
    window.beaconWatchdog = () => ({
      lastPlayheadAdvanceMs: Date.now() - lastPlayheadAdvanceRef.current,
      lastFragLoadMs: Date.now() - lastFragLoadAtRef.current,
      networkRecoveryAttempts: networkRecoveryAttemptsRef.current,
      remountCounter,
      degraded: degradedRef.current,
      recovering: recoveringRef.current,
      hlsLiveSyncPosition: hlsRef.current?.liveSyncPosition ?? null,
      videoCurrentTime: videoRef.current?.currentTime ?? null,
      videoDuration: videoRef.current?.duration ?? null,
      videoPaused: videoRef.current?.paused ?? null,
      videoVolume: videoRef.current?.volume ?? null,
      videoMuted: videoRef.current?.muted ?? null,
    });
    window.beaconForceRemount = () => {
      console.log("[beacon] forced remount via console");
      setRemountCounter((c) => c + 1);
    };
    // Degrade ladder: force a degrade-to-720 from the console to test the
    // held-frame + overlay path without waiting for a real stall.
    window.beaconForceDegrade = () => {
      console.log("[beacon] forced degrade-to-720 via console");
      degradeOrRemount720();
    };
    window.beaconEarlyBadge = () => ({
      phase: badgePhaseRef.current,
      earlyEntry,
      earlyEntryShowId,
      showtimeMs,
      msUntilShowtime: showtimeMs ? showtimeMs - Date.now() : null,
      syncingStartedAt: syncingStartedAtRef.current,
    });
    return () => {
      delete window.beaconSyncPos;
      delete window.beaconHandoff;
      delete window.beaconCueRunner;
      delete window.beaconWatchdog;
      delete window.beaconForceRemount;
      delete window.beaconForceDegrade;
      delete window.beaconEarlyBadge;
    };
  }, [remountCounter, earlyEntry, earlyEntryShowId, showtimeMs]);

  // ===== HUD updater (4b + 4d) =====
  useEffect(() => {
    const tick = () => {
      const showPos = computeSyncShowPos();
      const cuesNow = cuesRef.current;
      const a = anchorRef.current;
      const showId = a?.showId || null;
      const cursor = cueRunnerRef.current.cursor;
      const fired = cueRunnerRef.current.fired;

      if (showPos == null) {
        const k = `null|${showId}|${cuesNow.length}|${cursor}|${fired}`;
        if (lastHudKeyRef.current !== k) {
          lastHudKeyRef.current = k;
          setHudData({
            ready: false,
            showId,
            total: cuesNow.length,
            cursor,
            fired,
          });
        }
        return;
      }

      const showSec = Math.floor(showPos);
      const k = `${showSec}|${showId}|${cuesNow.length}|${cursor}|${fired}`;
      if (lastHudKeyRef.current === k) return;
      lastHudKeyRef.current = k;

      let nextCue = null;
      const showPosMs = showPos * 1000;
      for (let i = cursor; i < cuesNow.length; i++) {
        const c = cuesNow[i];
        if (c.tMs > showPosMs) {
          nextCue = c;
          break;
        }
      }

      setHudData({
        ready: true,
        showId,
        showSec,
        total: cuesNow.length,
        cursor,
        fired,
        nextCue,
        showPosMs,
      });
    };

    tick();
    const id = setInterval(tick, HUD_UPDATE_MS);
    return () => clearInterval(id);
  }, []);

  const stageClass = (() => {
    const classes = ["mb-doors-stage"];
    if (doorsPhase === "closing") classes.push("active");
    else if (doorsPhase === "ready") classes.push("active", "ready");
    else if (doorsPhase === "revealed") {
      classes.push("active", "ready", "revealed");
    } else if (doorsPhase === "error-flashing") {
      classes.push("active", "error-flashing");
    } else if (doorsPhase === "error") {
      classes.push("active", "error-flashing", "error");
    }
    if (showPatience) classes.push("patience");
    return classes.join(" ");
  })();

  const magicBadgeHidden = earlyEntry && badgePhase !== "hidden";

  const earlyBadgeClass = (() => {
    if (badgePhase === "hidden") return null;
    const classes = ["mb-status", "sf-early"];
    if (badgePhase === "countdown") classes.push("mb-state-countdown");
    else if (badgePhase === "syncing") classes.push("mb-state-syncing");
    else if (badgePhase === "locked") classes.push("mb-state-locked");
    else if (badgePhase === "exiting") {
      classes.push("mb-state-locked", "sf-early-exiting");
    }
    return classes.join(" ");
  })();

  const renderHud = () => {
    if (!hudData) return null;
    if (!hudData.ready) {
      return (
        <div className="sf-sync-hud">
          <div>—</div>
        </div>
      );
    }
    const mins = Math.floor(hudData.showSec / 60);
    const secs = String(hudData.showSec % 60).padStart(2, "0");
    let nextLine;
    if (hudData.nextCue) {
      const inSec = Math.max(
        0,
        Math.round(hudData.nextCue.tMs / 1000 - hudData.showSec)
      );
      nextLine = (
        <div className="sf-sync-hud-next">
          next: {hudData.nextCue.fx} in {inSec}s
        </div>
      );
    } else {
      nextLine = (
        <div className="sf-sync-hud-next sf-sync-hud-end">show complete</div>
      );
    }
    return (
      <div className="sf-sync-hud">
        <div>
          <strong>{prettyShow(hudData.showId)}</strong>
        </div>
        <div>
          ⏱ {mins}:{secs} · {hudData.cursor}/{hudData.total} reached ({hudData.fired} fired)
        </div>
        {nextLine}
      </div>
    );
  };

  return (
    <div className={`sync-fullscreen active${isTv ? " sf-tv" : ""}`}>
      <video
        ref={videoRef}
        className="sf-video"
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload noplaybackrate nofullscreen"
      />

      {/* Degrade-ladder recovery overlay — "Optimizing your stream…".
          Shown during rung 2/3 recovery (degrade to 720 / graceful
          remount) over the HELD last video frame so the customer never
          sees black. NO doors imagery — this is its own dim + message +
          animated dots so it reads as "working", not "broken" or
          "starting over". Fades out (sf-recovering-fading) the instant
          video resumes (FRAG_LOADED / playing → clearRecoveringOverlay).
          z-index sits above the video but BELOW the doors stage so a
          fresh entry's doors still win. */}
      {recovering.visible && (
        <div
          className={
            "sf-recovering" + (recovering.fading ? " sf-recovering-fading" : "")
          }
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            // Dim the held frame so the message is legible, but keep the
            // frame visible underneath — NOT a black fill.
            background: "rgba(6,8,20,0.62)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            fontFamily: "Lato, sans-serif",
            transition: `opacity ${RECOVERING_FADE_MS}ms ease`,
            opacity: recovering.fading ? 0 : 1,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              color: "#eaf2ff",
              fontSize: isTv ? "1.5vw" : "1.05rem",
              fontWeight: 800,
              letterSpacing: "0.4px",
              textShadow: "0 2px 12px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              gap: "0.5em",
            }}
          >
            <span>{RECOVERING_TEXT}</span>
            <span className="sf-recovering-dots" aria-hidden="true">
              <i></i>
              <i></i>
              <i></i>
            </span>
          </div>
          <style>{`
            .sf-recovering-dots {
              display: inline-flex;
              gap: 0.28em;
              align-items: center;
            }
            .sf-recovering-dots i {
              width: 0.46em;
              height: 0.46em;
              border-radius: 50%;
              background: #05f651;
              display: inline-block;
              opacity: 0.35;
              animation: sfRecoveringPulse 1.05s infinite ease-in-out;
            }
            .sf-recovering-dots i:nth-child(2) { animation-delay: 0.16s; }
            .sf-recovering-dots i:nth-child(3) { animation-delay: 0.32s; }
            @keyframes sfRecoveringPulse {
              0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
              40% { opacity: 1; transform: scale(1.1); }
            }
          `}</style>
        </div>
      )}

      {/* TV-only EXIT button. The remote lands on it (focused with green
          ring) after a short grace window so the user can press Select
          to return to multiview embeds. Red fill, white outline; green
          focus ring matches other TV remote affordances.

          The grace window matters: the Enter keypress that triggered
          sync entry (from the grid-config "1-screen" modal) is still
          bubbling when this button mounts — without the grace it would
          immediately fire onExit and bounce the user right back out.
          We disable Enter handling for the first 600ms, then focus. */}
      {isTv && doorsPhase !== "error" && (
        <SfTvExitButton
          onExit={onExit}
          outerRef={tvExitBtnRef}
          peerRef={needsControl ? tvTakeCtrlRef : null}
        />
      )}

      <div className="sf-streamer-label">@{streamerName || "streamer"}</div>
      {ctrlPhase !== "hidden" ? (
        isTv ? (
          <SfTvTakeControlBubble
            onTake={handleTakeControl}
            exitRef={tvExitBtnRef}
            selfRef={tvTakeCtrlRef}
            interactive={ctrlPhase === "pop" || ctrlPhase === "shown"}
            className={`sf-magic-badge sf-ctrl-badge sf-ctrl-${ctrlPhase}`}
          >
            {ctrlPhase === "granted" || ctrlPhase === "slideout" ? (
              <span>You're now in control!</span>
            ) : (
              <span>
                This device is NOT controlling your Magic Beacon.{" "}
                <span className="sf-ctrl-cta">CLICK HERE to take control now!</span>
              </span>
            )}
          </SfTvTakeControlBubble>
        ) : (
          <div
            role="button"
            className={`sf-magic-badge sf-ctrl-badge sf-ctrl-${ctrlPhase}`}
            onClick={
              ctrlPhase === "pop" || ctrlPhase === "shown"
                ? handleTakeControl
                : undefined
            }
          >
            {ctrlPhase === "granted" || ctrlPhase === "slideout" ? (
              <span>You're now in control!</span>
            ) : (
              <span>
                This device is NOT controlling your Magic Beacon.{" "}
                <span className="sf-ctrl-cta">TAP HERE to take control now!</span>
              </span>
            )}
          </div>
        )
      ) : (
        !magicBadgeHidden && (
          <div className="sf-magic-badge">MAGIC BEACON LIVE: CONNECTED!</div>
        )
      )}

      {earlyBadgeClass && (
        <div
          className={earlyBadgeClass}
          style={{
            // Dark gradient over night.jpg so text stays legible over the
            // fullscreen video. Set inline because css-loader can't resolve
            // a url() at build time. Alphas ~0.78 (darker than multitest2's
            // 0.55) per the readability preference.
            backgroundImage:
              "linear-gradient(160deg, rgba(8,8,24,0.80) 0%, rgba(22,8,40,0.76) 50%, rgba(8,8,24,0.80) 100%), url('/static/vip/night.jpg')",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
          }}
        >
          <div className="mb-status-inner">
            <div className="mb-status-label-wrap">
              {badgePhase !== "locked" && badgePhase !== "exiting" && (
                <div className="mb-status-label">
                  YOUR BANDS ARE SYNCED!
                </div>
              )}
            </div>
            <div className="mb-status-body">
              {badgeText && (
                <div className="mb-row">
                  <span className="mb-show">{badgeText.showName}</span>
                  {badgeText.detail && (
                    <span className="mb-detail">{badgeText.detail}</span>
                  )}
                  {badgeText.time && (
                    <span
                      className={
                        badgeText.urgent ? "mb-time mb-time-urgent" : "mb-time"
                      }
                    >
                      {badgeText.time}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {renderHud()}

      {/* 5b: peer-offer bubble — gold pill below the sf-early badge,
          shown during the syncing wait when a peer is locked on the same
          show. Tap → onPeerHandoff. Mirrors multitest2 #sfPeerBubble.
          On TV this is suppressed — BeaconMultiViewTV shows its own
          remote-friendly YES/NO peer overlay instead (the peer-pop sound
          still fires from the audio element below). */}
      {!isTv && peerBubble.streamId && (
        <div
          className={`sf-peer-bubble${peerBubble.exiting ? " sf-peer-out" : ""}`}
          style={{
            // Inline so the build's css-loader never tries to resolve the
            // image as a module. Gradient over night.jpg, exactly like
            // multitest2 (the gradient sits at ~0.6 alpha over the image).
            backgroundImage:
              "linear-gradient(160deg, rgba(10,10,31,0.62) 0%, rgba(26,10,46,0.58) 50%, rgba(10,10,31,0.62) 100%), url('/static/vip/night.jpg')",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            if (peerBubble.exiting) return;
            if (onPeerHandoff) onPeerHandoff(peerBubble.streamId);
          }}
        >
          Another Streamer Has Already Synced
          <br />
          TAP TO WATCH SOONER!
        </div>
      )}

      {/* Quick-swap offer — BOTH platforms (TV remote-trapped, Display
          taps the buttons). Same look/logic family as the peer-offer
          card: centered, friendly lime accent, YES/NO, 600ms arm grace.
          One-shot per session: answering either way sets
          quickSwapAnsweredRef so it never re-pops; NO also dismisses the
          peer for every other offer flow this session. */}
      {quickSwapOffer && (
        <SfTvPeerOverlay
          peerName={quickSwapOffer.name}
          accent="yellow"
          placement="center"
          title="THIS STREAM IS HAVING A ROUGH NIGHT"
          subtitle={`We found a smoother stream of this show. Switch now?`}
          onAccept={() => {
            quickSwapAnsweredRef.current = true;
            const target = quickSwapOffer.streamId;
            setQuickSwapOffer(null);
            if (target && onPeerHandoff) onPeerHandoff(target);
          }}
          onDecline={() => {
            quickSwapAnsweredRef.current = true;
            if (quickSwapOffer.streamId) {
              dismissedPeersRef.current.add(quickSwapOffer.streamId);
            }
            setQuickSwapOffer(null);
          }}
        />
      )}

      {/* TV-only HLS peer-handoff overlay. Replaces the gold tap-bubble
          (suppressed above) AND covers the stream-warning peerId case
          (the stream-warning bar still renders for the no-peer cases).
          Wording adapts: pre-show / sync-wait says "switch sooner",
          mid-show health says "switch now". YES focused with 600ms grace. */}
      {isTv &&
        (peerBubble.streamId || (streamWarning.peerId && (streamWarning.stage === 1 || streamWarning.stage === 2))) && (
          <SfTvPeerOverlay
            peerName={peerBubble.name || streamWarning.peerName || "another streamer"}
            accent={
              streamWarning.peerId && streamWarning.stage === 2
                ? "red"
                : "yellow"
            }
            placement={streamWarning.peerId ? "corner" : "center"}
            title={
              streamWarning.peerId
                ? streamWarning.stage === 2
                  ? "STREAMER ISSUE PERSISTING"
                  : "STREAMER ISSUE DETECTED"
                : "Another Streamer Has Already Synced"
            }
            subtitle={
              streamWarning.peerId
                ? streamWarning.stage === 2
                  ? "Press YES to switch streamers now"
                  : "Press YES to switch streamers"
                : "Press YES to watch sooner!"
            }
            onAccept={() => {
              const target = peerBubble.streamId || streamWarning.peerId;
              if (target && onPeerHandoff) onPeerHandoff(target);
            }}
            onDecline={() => {
              // Remember this peer was dismissed — without this, the
              // streamWarning recompute will re-detect the same peer on
              // the next anchor tick and re-pop the overlay endlessly.
              const dismissed =
                peerBubble.streamId || streamWarning.peerId;
              if (dismissed) {
                dismissedPeersRef.current.add(dismissed);
              }
              setPeerBubble({ streamId: null, name: null, exiting: false });
              setStreamWarning((prev) => ({ ...prev, peerId: null }));
            }}
          />
        )}

      <audio
        ref={peerPopRef}
        preload="auto"
        src="/assets/doors/peer-pop.mp3"
      />

      {/* 5c: stream-health warning bar. Yellow (stage 1) / red (stage 2)
          pill near the top of the HLS view. When a peer is locked on the
          same show, the whole bar is a one-tap handoff. Driven by
          anchor.streamHealthWarning. Mirrors multitest2 #sfStreamWarning. */}
      {(streamWarning.stage === 1 || streamWarning.stage === 2) &&
        !(isTv && streamWarning.peerId) && (
        <div
          className={
            `sf-stream-warning stage-${streamWarning.stage}` +
            (streamWarning.shake ? " sf-warning-shake" : "")
          }
          style={{
            // Inline gradient + night.jpg (css-loader can't resolve url()).
            backgroundImage:
              "linear-gradient(160deg, rgba(10,10,31,0.62) 0%, rgba(26,10,46,0.58) 50%, rgba(10,10,31,0.62) 100%), url('/static/vip/night.jpg')",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
            cursor: streamWarning.peerId ? "pointer" : "default",
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            if (streamWarning.peerId && onPeerHandoff) {
              onPeerHandoff(streamWarning.peerId);
            }
          }}
        >
          <div className="mb-status-inner">
            <div className="mb-status-label-wrap">
              <div className="mb-status-label">
                {streamWarning.stage === 1
                  ? "STREAMER ISSUE DETECTED"
                  : "STREAMER ISSUE PERSISTING"}
              </div>
            </div>
            <div className="mb-status-body">
              {streamWarning.stage === 1
                ? streamWarning.peerId
                  ? "TAP to switch streamers"
                  : "Readjusting…"
                : streamWarning.peerId
                ? "TAP to switch streamers now"
                : "Playback will be affected"}
            </div>
          </div>
        </div>
      )}

      <audio
        ref={warnBuzzRef}
        preload="auto"
        src="/assets/doors/error.mp3"
      />

      {/* 5d: Tink Tracker — HEA-only radar with pixie-flyoff at 17:21.
          isHea gate is post-lock (anchor showId), so it only appears once
          a real anchor confirms the show. Driven by the cue-runner showPos
          via tinkShowPosRef. The user toggle (tinkUserEnabled) slides it
          off-screen-right without ending the session. */}
      <TinkTracker
        enabled={tinkUserEnabled !== false}
        isHea={anchor?.showId === "happily-ever-after"}
        showPosRef={tinkShowPosRef}
      />

      <audio
        ref={chimeRef}
        preload="auto"
        src="/assets/doors/transition_chime.mp3"
      />
      <audio
        ref={connectionChimeRef}
        preload="auto"
        src="/assets/doors/connection-chime.mp3"
      />
      <div className={stageClass}>
        <img
          className="mb-transition-img mb-transition-base"
          src="/assets/doors/transition_bg.jpg"
          alt=""
        />
        <img
          className="mb-transition-img mb-transition-pulse"
          src="/assets/doors/transition_pulse.jpg"
          alt=""
        />
        <img
          className="mb-transition-img mb-transition-green"
          src="/assets/doors/transition_green.jpg"
          alt=""
        />
        <img
          className="mb-transition-img mb-transition-error-pulse"
          src="/assets/doors/transition_error.jpg"
          alt=""
        />
        <div className="mb-doors-patience">
          <div className="mb-doors-patience-text">
            Taking a bit longer than expected... please be patient
          </div>
        </div>
        <div className="mb-doors-error">
          <div className="mb-doors-error-card">
            <p>
              We're sorry, this stream had trouble loading. We recommend
              trying again below. If the issue persists, please choose
              another stream.
            </p>
            {isTv ? (
              // TV: two-button focus trap (TRY AGAIN + EXIT), modeled on
              // the peer overlay's Left/Right navigation. TRY AGAIN gets
              // initial focus after the standard grace window; the same
              // 600ms grace prevents the keypress that surfaced the error
              // from immediately firing either action.
              //
              // CRITICAL: only mount when the error is actually visible
              // (doorsPhase === "error") so it never races the bottom-right
              // EXIT button for focus during normal doors runs.
              doorsPhase === "error" ? (
                <SfTvErrorButtons
                  onRetry={() => {
                    console.log("[beacon] doors: user retry from error card");
                    setRemountCounter((c) => c + 1);
                    setDoorsAttempt((a) => a + 1);
                  }}
                  onExit={onExit}
                />
              ) : null
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "14px",
                  justifyContent: "center",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    console.log("[beacon] doors: user retry from error card");
                    setRemountCounter((c) => c + 1);
                    setDoorsAttempt((a) => a + 1);
                  }}
                >
                  Try Again
                </button>
                <button type="button" onClick={onExit}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SyncFullscreen;
